export type Lang = 'sq' | 'en';

export const LANGUAGES: Lang[] = ['sq', 'en'];

// One heading + one or more short paragraphs — used for every section of
// the /terms and /privacy pages so both languages render the same shape.
interface LegalSection {
  heading: string;
  body: string[];
}

// Namespaces are added incrementally, one per implementation checkpoint.
// Both `sq` and `en` must satisfy this interface in full — a missing
// translation for either language is a TypeScript compile error, not a
// silent runtime gap.
export interface Translations {
  common: {
    loading: string;
    save: string;
    saving: string;
    cancel: string;
    yes: string;
    no: string;
    close: string;
    back: string;
    continue: string;
    confirm: string;
    error: string;
    success: string;
    edit: string;
    delete: string;
    add: string;
    search: string;
  };
  booking: {
    brandSuffix: string; // "Online" in "Rezervo Online" — kept for symmetry, currently unused directly
    poweredBy: string;
    loading: string;
    notFoundTitle: string;
    notFoundBody: string;
    backToHome: string;

    weekdaysShort: [string, string, string, string, string, string, string]; // Mon..Sun
    months: [
      string,
      string,
      string,
      string,
      string,
      string,
      string,
      string,
      string,
      string,
      string,
      string,
    ]; // January..December

    steps: {
      service: string;
      datetime: string;
      info: string;
    };

    service: {
      title: string;
      subtitle: string;
      empty: string;
      durationMinutes: (n: number) => string;
      cancelExisting: string;
    };

    datetime: {
      title: string;
      durationShort: (n: number) => string;
      availableLegend: string;
      bookedLegend: string;
      noSlotsTitle: string;
      noSlotsQuestion: string;
      notifyMe: string;
      noThanks: string;
      fullNameLabel: string;
      fullNamePlaceholder: string;
      emailLabel: string;
      emailPlaceholder: string;
      phoneOptionalLabel: string;
      phonePlaceholder: string;
      addToWaitlist: string;
      savingEllipsis: string;
      waitlistSuccessTitle: string;
      waitlistSuccessBody: string;
      availableTimesLabel: string;
      noSlotsForDay: string;
      continueCta: string;
    };

    info: {
      title: string;
      phoneLabel: string;
      notesOptionalLabel: string;
      notesPlaceholder: string;
      confirmCta: string;
      sendingEllipsis: string;
    };

    cancelModal: {
      title: string;
      intro: string;
      bookingIdLabel: string;
      bookingIdPlaceholder: string;
      yourEmailLabel: string;
      findBooking: string;
      searchingEllipsis: string;
      notFound: string;
      genericError: string;
      cancelFailed: string;
      confirmHeading: string;
      clientLabel: string;
      serviceLabel: string;
      dateLabel: string;
      irreversibleWarning: string;
      confirmCancelYes: string;
      cancellingEllipsis: string;
      cancelSuccessTitle: string;
      cancelSuccessBody: string;
    };

    tokenCancel: {
      invalid: string;
      backToBooking: string;
      cancelledTitle: string;
      cancelledBody: string;
      bookAgain: string;
      title: string;
      irreversibleWarning: string;
      confirmCancelYes: string;
      cancellingEllipsis: string;
      cancelFailed: string;
    };

    earlierSlot: {
      confirmedTitle: string;
      question: string;
      yesNotifyMe: string;
      savingEllipsis: string;
      done: string;
      // Shown when the page is opened via a "Shiko oraret" email link
      // (?rebook_ref=...) — the customer is replacing an existing
      // booking with an earlier one, not making an unrelated new one.
      replacementNotice: string;
      // Shown before email verification, on first opening a rebook_ref
      // link (get_replaceable_booking_context has not resolved yet).
      verifyIntro: string;
      verifyEmailPrompt: string;
      verifyButton: string;
      verifyingEllipsis: string;
    };

    confirmation: {
      confirmedTitle: string;
      pendingTitle: string;
      bookingIdLabel: string;
      dateLabel: string;
      timeLabel: string;
      clientLabel: string;
      emailLabel: string;
      phoneLabel: string;
      addToCalendar: string;
      googleCalendar: string;
      share: string;
      linkCopied: string;
      copyLink: string;
      shareAction: string;
      confirmedBannerTitle: string;
      confirmedBannerBody: string;
      pendingBannerTitle: string;
      pendingBannerBody: string;
      bookAgain: string;
      homePage: string;
      cancelThisBooking: string;
      earlierSlotQuestion: string;
    };

    errors: {
      noAvailability: string;
      businessClosed: string;
      businessClosedThatDay: string;
      outsideBusinessHours: string;
      dateInPast: string;
      slotInPast: string;
      businessNotFound: string;
      serviceNotFound: string;
      clientNameRequired: string;
      clientEmailRequired: string;
      clientPhoneRequired: string;
      bookingsUnavailable: string;
      generic: string;
      // replace_public_booking's own error codes (earlier-slot
      // replacement flow only).
      bookingNotFound: string;
      originalBookingNotActive: string;
      originalBookingNotInFuture: string;
      noEligibleEarlierSlotPreference: string;
      newSlotNotEarlier: string;
      replacementAlreadyInProgress: string;
    };
  };
  dashboard: {
    reservationStatus: {
      all: string;
      active: string;
      pending: string;
      completed: string;
      cancelled: string;
      rejected: string;
    };
    calendar: {
      noServicesAssigned: string;
      emailOptionalHint: string;
    };
    notifications: {
      newBooking: string;
      bookingCancelled: string;
      generic: string;
      title: string;
      markAllRead: string;
      empty: string;
      viewAllBookings: string;
      // Real-time toast type labels — kept distinct from
      // reservationStatus.cancelled, which describes a booking's own
      // status, not "this toast reports a cancellation."
      toastNew: string;
      toastCancelled: string;
      toastUpdated: string;
    };
    notificationSettings: {
      emailLabel: string;
      emailPlaceholder: string;
      modeAll: string;
      modeAllDesc: string;
      modeImportant: string;
      modeImportantDesc: string;
      modeNone: string;
      modeNoneDesc: string;
      alwaysActiveNote: string;
      saveButton: string;
      saved: string;
    };
    // Internal keys (Tab ids / day-of-week keys) are stable identifiers,
    // never translated themselves — only the displayed label is.
    nav: {
      overview: string;
      subscription: string;
      bookings: string;
      schedule: string;
      waitinglist: string;
      analytics: string;
      services: string;
      clients: string;
      staff: string;
      profile: string;
      settings: string;
      sectionMain: string;
      sectionManagement: string;
      sectionAccount: string;
      signOut: string;
      publicPage: string;
    };
    days: {
      monday: string; tuesday: string; wednesday: string; thursday: string;
      friday: string; saturday: string; sunday: string;
    };
    // Display labels for the fixed CATEGORIES value list (dashboard/
    // page.tsx). Keyed by the same stable labelKey each CATEGORIES entry
    // carries — NEVER by the persisted value itself, which must stay
    // exactly as stored in businesses.category regardless of language.
    categories: {
      beautySalon: string;
      barber: string;
      dentalClinic: string;
      medicalClinic: string;
      spaWellness: string;
      gymFitness: string;
      photographer: string;
      consultant: string;
      lawyer: string;
      accountant: string;
      aestheticCenter: string;
      nailArtist: string;
      other: string;
    };
    header: {
      themeLight: string;
      themeDark: string;
      themeLightLabel: string;
      themeDarkLabel: string;
      copied: string;
      copyLink: string;
    };
    overview: {
      welcomePrefix: string;
      businessFallback: string;
      subtitle: string;
      statTodayBookings: string;
      statTodaySub: string;
      statActiveBookings: string;
      statActiveSub: string;
      statCompleted: string;
      statCompletedSub: string;
      statRevenue: string;
      statRevenueSub: string;
      chartLast7Days: string;
      todayBookingsTitle: string;
      viewAll: string;
      noBookingsToday: string;
      quickActionAddService: string;
      quickActionManageHours: string;
      quickActionViewPublic: string;
      trialEndedTitle: string;
      trialEndingSoon: (days: number) => string;
      trialEndedBody: string;
      trialEndingBody: string;
      addCardCta: string;
      trialInfoLabel: string;
      trialInfoDaysLeft: (days: number) => string;
      trialInfoSuffix: string;
      viewSubscription: string;
    };
    bookings: {
      title: string;
      totalCount: (n: number) => string;
      searchPlaceholder: string;
      emptyTitle: string;
      confirmQuestion: string;
      confirmAction: string;
      rejectAction: string;
      rejectConfirmQuestion: string;
      editModalTitle: string;
      clientNameLabel: string;
      emailLabel: string;
      dateLabel: string;
      timeLabel: string;
      serviceLabelOptional: string;
      keepCurrentService: string;
      notesLabel: string;
      saveChanges: string;
    };
    waitingList: {
      title: string;
      summary: (total: number, waiting: number) => string;
      tabWaitingList: string;
      tabEarlierSlots: string;
      waitingListHint: string;
      earlierSlotsHint: string;
      filterAll: string;
      emptyWaitingList: string;
      emptyWaitingListFiltered: (label: string) => string;
      emptyWaitingListHint: string;
      emptyEarlierSlots: string;
      emptyEarlierSlotsHint: string;
      registered: (date: string) => string;
      notifiedAt: (date: string) => string;
      rescheduledAt: (date: string) => string;
      currentBooking: (date: string, time: string) => string;
      notifyAction: string;
      bookedAction: string;
      expiredAction: string;
      statusActive: string;
      statusInactive: string;
      statusRescheduled: string;
      statusNotified: string;
      waitingStatus: { waiting: string; notified: string; booked: string; expired: string };
    };
    tabError: {
      message: string;
      retry: string;
    };
    clients: {
      title: string;
      uniqueCount: (n: number) => string;
      emptyTitle: string;
      emptyHint: string;
      emailAction: string;
      lastBooking: (date: string) => string;
    };
    staff: {
      title: string;
      activeCount: (n: number) => string;
      addMember: string;
      editMember: string;
      newMember: string;
      nameLabel: string;
      namePlaceholder: string;
      roleLabel: string;
      rolePlaceholder: string;
      emailLabel: string;
      phoneLabel: string;
      colorLabel: string;
      servicesPerformed: string;
      weeklySchedule: string;
      configureBusinessHoursFirst: string;
      working: string;
      onLeave: string;
      businessClosedShort: string;
      addFirstMember: string;
      noStaffYet: string;
      active: string;
      inactive: string;
    };
    schedule: {
      title: string;
      selectDate: string;
      serviceLabel: string;
      allServices: string;
      staffLabel: string;
      allStaff: string;
      addBooking: string;
      manage: string;
      free: string;
      booked: string;
      onLeaveLegend: string;
      outsideHours: string;
      closedLegend: string;
      businessClosurePrefix: string;
      businessClosedPeriod: string;
      businessClosedDay: string;
      noActiveStaff: string;
      noActiveStaffForService: string;
      notWorkingToday: string;
      onLeaveFullDay: string;
      slotsFree: (n: number) => string;
      slotsBooked: (n: number) => string;
      businessHours: string;
      noSchedulesConfigured: string;
      createDefaultSchedules: string;
      creating: string;
      minIntervalSuffix: string;
      open: string;
      closed: string;
      addBookingModalTitle: string;
      bookingCreatedSuccess: string;
      dateLabel: string;
      timeLabel: string;
      selectService: string;
      selectStaffRequired: string;
      noStaff: string;
      clientNameLabel: string;
      clientNamePlaceholder: string;
      phoneLabel: string;
      emailOptionalLabel: string;
      manageModalTitle: string;
      businessClosureTab: string;
      staffLeaveTab: string;
      closureIntro: string;
      fromDate: string;
      toDate: string;
      reasonOptional: string;
      closureReasonPlaceholder: string;
      leaveReasonPlaceholder: string;
      leaveIntro: string;
      staffMemberLabel: string;
      selectMember: string;
      leaveDateLabel: string;
      fullDay: string;
      specificHours: string;
      fromTime: string;
      toTime: string;
      conflictsWarning: (n: number) => string;
      confirmSaveAgain: string;
      confirmAndSave: string;
      existing: string;
      staffRequiredError: string;
      savedChanges: string;
      endDateBeforeStartError: string;
      allDatesAlreadyHaveLeave: string;
      savedWithSkippedDates: (saved: number, skipped: number) => string;
    };
    services: {
      title: string;
      count: (n: number) => string;
      addService: string;
      editService: string;
      newService: string;
      nameLabel: string;
      namePlaceholder: string;
      priceLabel: string;
      optionalSuffix: string;
      pricePlaceholder: string;
      durationLabel: string;
      descriptionLabel: string;
      descriptionPlaceholder: string;
      addServiceBtn: string;
      noServicesYet: string;
      addFirstService: string;
      active: string;
      inactive: string;
    };
    profile: {
      title: string;
      subtitle: string;
      photoLabel: string;
      uploadPhoto: string;
      changePhoto: string;
      removePhoto: string;
      photoHint: string;
      nameLabel: string;
      namePlaceholder: string;
      categoryLabel: string;
      selectCategory: string;
      phoneLabel: string;
      websiteLabel: string;
      addressLabel: string;
      addressPlaceholder: string;
      cityLabel: string;
      cityPlaceholder: string;
      descriptionLabel: string;
      descriptionPlaceholder: string;
      saveProfile: string;
      saved: string;
      savedSuccess: string;
      publicPreview: string;
      businessNameFallback: string;
      bookingLink: string;
      copy: string;
    };
    settings: {
      title: string;
      subtitle: string;
      account: string;
      businessFallback: string;
      appearance: string;
      displayMode: string;
      homepageAlwaysDark: string;
      notifications: string;
      autoConfirm: string;
      autoConfirmOnDesc: string;
      autoConfirmOffDesc: string;
      bookingLinkTitle: string;
      bookingLinkDesc: string;
      copied: string;
      viewPublicPage: string;
      dangerZone: string;
      irreversibleActions: string;
      signOut: string;
    };
    subscription: {
      title: string;
      subtitle: string;
      statusActive: string;
      statusTrial: string;
      statusPastDue: string;
      statusCancelled: string;
      statusPaymentRequired: string;
      daysLeftFromTrial: (n: number) => string;
      trialEnded: string;
      fullyActive: string;
      addPaymentToContinue: string;
      badgeTrial: string;
      badgeActive: string;
      badgePastDue: string;
      badgeCancelled: string;
      badgePaymentRequired: string;
      trialProgress: string;
      daysOf30: (n: number) => string;
      started: string;
      ends: string;
      trialBodyText: string;
      activeBodyText: string;
      inactiveBodyText: string;
      activatedSuccess: string;
      paypalConfirmingText: string;
      activateWithPaypal: string;
      priceLine: (price: string) => string;
      retryLoadingPaypal: string;
      loadingPaypal: string;
      paypalGenericError: string;
      paypalCancelledError: string;
      paypalSecureNote: string;
      supabaseConfigMissing: string;
      paypalConfigMissing: string;
      paypalLoadError: string;
      whatsIncluded: string;
      features: [string, string, string, string, string, string];
    };
    analytics: {
      title: string;
      subtitle: string;
      period7d: string;
      period30d: string;
      period90d: string;
      totalBookings: string;
      revenue: string;
      completionRate: string;
      uniqueClients: string;
      dailyBookings: string;
      bookingStatus: string;
      noData: string;
      dailyRevenue: string;
      topServices: string;
      chartBookingsLabel: string;
      chartRevenueLabel: string;
    };
    errors: {
      saveFailed: string;
      loadFailed: string;
      uploadFailed: string;
      createFailed: string;
      photoTooLarge: string;
      nameRequired: string;
      roleRequired: string;
      staffSaveFailed: string;
      businessClosedAt: (day: string) => string;
      openBeforeClose: string;
      staffHoursWithinBusiness: (open: string, close: string) => string;
    };
    addBookingErrors: {
      clientNameRequired: string;
      businessNotFound: string;
      serviceNotFound: string;
      dateInPast: string;
      slotInPast: string;
      businessClosed: string;
      businessClosedThatDay: string;
      outsideBusinessHours: string;
      staffRequired: string;
      staffNotFound: string;
      staffNotEligible: string;
      noAvailability: string;
      notAuthorized: string;
      generic: string;
      fillRequiredFields: string;
    };
  };
  legal: {
    lastUpdated: string;
    footerTerms: string;
    footerPrivacy: string;
    contactHeading: string;
    contactIntro: string;
    bookingNotice: {
      prefix: string;
      linkText: string;
      suffix: string;
    };
    registerNotice: {
      prefix: string;
      termsLink: string;
      middle: string;
      privacyLink: string;
      suffix: string;
    };
    terms: {
      title: string;
      intro: string;
      sections: {
        about: LegalSection;
        responsibilities: LegalSection;
        subscription: LegalSection;
        acceptableUse: LegalSection;
        accountSecurity: LegalSection;
        availability: LegalSection;
        liability: LegalSection;
        termination: LegalSection;
        changes: LegalSection;
        governingLaw: LegalSection;
      };
    };
    privacy: {
      title: string;
      intro: string;
      sections: {
        dataCollected: LegalSection;
        purposes: LegalSection;
        lawfulBasis: LegalSection;
        controllerProcessor: LegalSection;
        thirdParties: LegalSection;
        dataSharing: LegalSection;
        international: LegalSection;
        security: LegalSection;
        retention: LegalSection;
        rights: LegalSection;
        cookies: LegalSection;
        changes: LegalSection;
      };
    };
  };
  register: {
    tagline: string;
    stepLabels: [string, string, string];
    alreadyHaveAccount: string;
    loginHere: string;
    continueCta: string;
    savingCta: string;
    step1: {
      title: string;
      subtitle: string;
      businessNameLabel: string;
      businessNamePlaceholder: string;
      emailLabel: string;
      emailPlaceholder: string;
      phoneLabel: string;
      phonePlaceholder: string;
      passwordLabel: string;
      passwordPlaceholder: string;
      creatingAccountCta: string;
    };
    step2: {
      title: string;
      subtitle: string;
      categoryLabel: string;
      categoryPlaceholder: string;
      cityLabel: string;
      cityPlaceholder: string;
      addressLabel: string;
      addressPlaceholder: string;
      descriptionLabel: string;
      descriptionPlaceholder: string;
    };
    step3: {
      title: string;
      subtitle: string;
      bookingIntervalLabel: string;
      bookingIntervalHelp: string;
      weeklyScheduleLabel: string;
      closedLabel: string;
      finishCta: string;
    };
    errors: {
      businessNameRequired: string;
      emailRequired: string;
      passwordTooShort: string;
      categoryRequired: string;
      cityRequired: string;
      businessIdMissing: string;
      generic: string;
      authenticationRequired: string;
      businessAlreadyExists: string;
      slugGenerationFailed: string;
    };
  };
  admin: {
    nav: { overview: string; businesses: string };
    header: { title: string; badge: string; backToDashboard: string };
    accessDenied: { title: string; body: string; backToDashboard: string };
    loading: string;
    loadError: string;
    overview: {
      totalBusinesses: string;
      testBusinesses: string;
      trialBusinesses: string;
      activeBusinesses: string;
      expiredBlockedBusinesses: string;
      totalBookings: string;
      totalCustomers: string;
      totalServices: string;
      totalStaff: string;
      bookingsToday: string;
      bookingsLast7Days: string;
      newBusinessesLast30Days: string;
    };
    businesses: {
      title: string;
      searchPlaceholder: string;
      filterAll: string;
      filterTrial: string;
      filterActive: string;
      filterExpiredBlocked: string;
      filterTest: string;
      categoryAll: string;
      columns: {
        name: string;
        owner: string;
        phone: string;
        category: string;
        city: string;
        created: string;
        trialEnd: string;
        status: string;
        bookingStatus: string;
        paypal: string;
        test: string;
        services: string;
        staff: string;
        bookings: string;
        customers: string;
      };
      empty: string;
      prev: string;
      next: string;
      pageInfo: (from: number, to: number, total: number) => string;
      viewDetail: string;
      none: string;
    };
    status: { trial: string; active: string; expiredBlocked: string; test: string; enabled: string; blocked: string };
    detail: {
      title: string;
      basic: string;
      subscription: string;
      usage: string;
      slug: string;
      owner: string;
      phone: string;
      category: string;
      city: string;
      created: string;
      website: string;
      none: string;
      trialStart: string;
      trialEnd: string;
      status: string;
      bookingStatus: string;
      paypal: string;
      testFlag: string;
      totalBookings: string;
      bookingsLast30Days: string;
      clients: string;
      services: string;
      staff: string;
      waitingList: string;
      lastBooking: string;
      never: string;
      openPublicPage: string;
      close: string;
    };
  };
  pwa: {
    dashboardCard: {
      title: string;
      body: string;
      androidCta: string;
      iosCta: string;
      installedLabel: string;
    };
    iosSheet: {
      title: string;
      step1: string;
      step2: string;
      step3: string;
      footer: string;
    };
    registerSuccess: {
      heading: string;
      body: string;
      continueCta: string;
    };
  };
}

export const translations: Record<Lang, Translations> = {
  sq: {
    common: {
      loading: 'Duke ngarkuar...',
      save: 'Ruaj',
      saving: 'Duke ruajtur...',
      cancel: 'Anulo',
      yes: 'Po',
      no: 'Jo',
      close: 'Mbyll',
      back: 'Mbrapa',
      continue: 'Vazhdo',
      confirm: 'Konfirmo',
      error: 'Ndodhi një gabim.',
      success: 'U krye me sukses.',
      edit: 'Ndrysho',
      delete: 'Fshi',
      add: 'Shto',
      search: 'Kërko',
    },
    booking: {
      brandSuffix: 'Online',
      poweredBy: 'Mundësuar nga',
      loading: 'Duke ngarkuar...',
      notFoundTitle: 'Biznesi nuk u gjet',
      notFoundBody: 'Linku mund të jetë i gabuar ose biznesi nuk është aktiv.',
      backToHome: 'Kthehu në faqen kryesore',

      weekdaysShort: ['Hën', 'Mar', 'Mër', 'Enj', 'Pre', 'Sht', 'Die'],
      months: [
        'Janar',
        'Shkurt',
        'Mars',
        'Prill',
        'Maj',
        'Qershor',
        'Korrik',
        'Gusht',
        'Shtator',
        'Tetor',
        'Nëntor',
        'Dhjetor',
      ],

      steps: {
        service: 'Shërbimi',
        datetime: 'Data & Ora',
        info: 'Të dhënat',
      },

      service: {
        title: 'Zgjedh shërbimin',
        subtitle: 'Çfarë shërbimi dëshironi?',
        empty: 'Nuk ka shërbime të disponueshme.',
        durationMinutes: (n: number) => `${n} minuta`,
        cancelExisting: 'Anulo një rezervim ekzistues',
      },

      datetime: {
        title: 'Zgjedh datën dhe orën',
        durationShort: (n: number) => `${n} min`,
        availableLegend: 'I disponueshëm',
        bookedLegend: 'I zënë',
        noSlotsTitle: 'Nuk ka orare të lira për momentin.',
        noSlotsQuestion: 'A dëshironi t’ju njoftojmë nëse lirohet një orar?',
        notifyMe: 'Po, më njoftoni',
        noThanks: 'Jo, faleminderit',
        fullNameLabel: 'Emri dhe mbiemri',
        fullNamePlaceholder: 'Arta Kelmendi',
        emailLabel: 'Email',
        emailPlaceholder: 'arta@email.com',
        phoneOptionalLabel: 'Telefon (opsionale)',
        phonePlaceholder: '+355 69 123 4567',
        addToWaitlist: 'Shto në listën e pritjes',
        savingEllipsis: 'Duke ruajtur...',
        waitlistSuccessTitle: 'U shtuat në listën e pritjes.',
        waitlistSuccessBody: 'Do t’ju njoftojmë sapo të lirohet një orar.',
        availableTimesLabel: 'Ora disponueshme',
        noSlotsForDay: 'Nuk ka orare të disponueshme për këtë ditë.',
        continueCta: 'Vazhdo',
      },

      info: {
        title: 'Të dhënat tuaja',
        phoneLabel: 'Numri i telefonit',
        notesOptionalLabel: 'Shënime (opsionale)',
        notesPlaceholder: 'Ndonjë kërkesë e veçantë...',
        confirmCta: 'Konfirmo rezervimin',
        sendingEllipsis: 'Duke dërguar...',
      },

      cancelModal: {
        title: 'Anulo rezervimin',
        intro:
          'Vendosni Booking ID-në (nga email-i ose ekrani i konfirmimit) dhe email-in tuaj për të gjetur rezervimin.',
        bookingIdLabel: 'Booking ID',
        bookingIdPlaceholder: 'p.sh. RZV-AB12CD',
        yourEmailLabel: 'Email-i juaj',
        findBooking: 'Gjej rezervimin',
        searchingEllipsis: 'Duke kërkuar...',
        notFound: 'Rezervimi nuk u gjet. Kontrolloni ID-në dhe email-in.',
        genericError: 'Ndodhi një gabim. Provo përsëri.',
        cancelFailed: 'Anulimi dështoi. Provo përsëri.',
        confirmHeading: 'Konfirmo anulimin',
        clientLabel: 'Klient:',
        serviceLabel: 'Shërbimi:',
        dateLabel: 'Data:',
        irreversibleWarning: 'Ky veprim nuk mund të kthehet mbrapsht. Jeni të sigurt?',
        confirmCancelYes: 'Po, anulo rezervimin',
        cancellingEllipsis: 'Duke anuluar...',
        cancelSuccessTitle: 'Rezervimi u anulua me sukses.',
        cancelSuccessBody: 'Orari është liruar dhe mund të rezervohet nga klientë të tjerë.',
      },

      tokenCancel: {
        invalid: 'Ky link anulimi nuk është i vlefshëm.',
        backToBooking: 'Kthehu te faqja e rezervimeve',
        cancelledTitle: 'Rezervimi u anulua.',
        cancelledBody: 'Orari është liruar.',
        bookAgain: 'Bëj një rezervim tjetër',
        title: 'Anulo rezervimin',
        irreversibleWarning: 'Ky veprim nuk mund të kthehet mbrapsht. Jeni të sigurt?',
        confirmCancelYes: 'Po, anulo rezervimin',
        cancellingEllipsis: 'Duke anuluar...',
        cancelFailed: 'Anulimi dështoi. Provo përsëri.',
      },

      earlierSlot: {
        confirmedTitle: 'Rezervimi juaj u konfirmua.',
        question: 'A dëshironi t’ju njoftojmë nëse lirohet një orar më parë?',
        yesNotifyMe: 'Po, më njoftoni',
        savingEllipsis: 'Duke ruajtur...',
        done: 'Do t’ju njoftojmë nëse lirohet një orar më herët!',
        replacementNotice: 'Po zgjedh një orar më të hershëm. Rezervimi aktual do të anulohet vetëm pasi orari i ri të konfirmohet.',
        verifyIntro: 'Po zgjedh një orar më të hershëm për rezervimin tënd aktual.',
        verifyEmailPrompt: 'Konfirmo emailin e rezervimit për të vazhduar.',
        verifyButton: 'Vazhdo',
        verifyingEllipsis: 'Duke verifikuar...',
      },

      confirmation: {
        confirmedTitle: 'Rezervimi u konfirmua!',
        pendingTitle: 'Kërkesa për rezervim u dërgua',
        bookingIdLabel: 'Booking ID:',
        dateLabel: 'Data',
        timeLabel: 'Ora',
        clientLabel: 'Klienti',
        emailLabel: 'Email',
        phoneLabel: 'Telefoni',
        addToCalendar: 'Shto në Kalendar',
        googleCalendar: 'Google Calendar',
        share: 'Ndaj me të tjerët',
        linkCopied: 'U kopjua!',
        copyLink: 'Kopjo linkun',
        shareAction: 'Shpërndaje',
        confirmedBannerTitle: 'Rezervimi u konfirmua!',
        confirmedBannerBody: 'Rezervimi juaj është konfirmuar. Shihemi në datën e caktuar!',
        pendingBannerTitle: 'Në pritje të konfirmimit',
        pendingBannerBody: 'Biznesi do ta shqyrtojë dhe konfirmojë rezervimin tuaj së shpejti. Do t\'ju njoftojmë me email.',
        bookAgain: 'Bëj rezervim tjetër',
        homePage: 'Faqja kryesore',
        cancelThisBooking: 'Anulo këtë rezervim',
        earlierSlotQuestion: 'A dëshironi të njoftoheni nëse lirohet një orar më parë?',
      },

      errors: {
        noAvailability: 'Ky orar sapo u zu nga dikush tjetër. Ju lutem zgjidhni një orar tjetër.',
        businessClosed: 'Biznesi është i mbyllur në këtë datë.',
        businessClosedThatDay: 'Biznesi është i mbyllur në këtë ditë.',
        outsideBusinessHours: 'Ora e zgjedhur është jashtë orarit të punës.',
        dateInPast: 'Nuk mund të rezervoni në një datë të kaluar.',
        slotInPast: 'Ky orar nuk është më i disponueshëm. Ju lutemi zgjidhni një orar tjetër.',
        businessNotFound: 'Biznesi nuk u gjet.',
        serviceNotFound: 'Shërbimi nuk u gjet ose nuk është më aktiv.',
        clientNameRequired: 'Emri është i detyrueshëm.',
        clientEmailRequired: 'Email-i është i detyrueshëm.',
        clientPhoneRequired: 'Ju lutemi shkruani numrin e telefonit.',
        bookingsUnavailable: 'Rezervimet online nuk janë aktualisht të disponueshme.',
        generic: 'Ndodhi një gabim. Provo përsëri.',
        bookingNotFound: 'Nuk gjetëm rezervimin tuaj. Kontrolloni kodin dhe email-in.',
        originalBookingNotActive: 'Rezervimi juaj aktual nuk është më aktiv.',
        originalBookingNotInFuture: 'Rezervimi juaj aktual tashmë ka kaluar.',
        noEligibleEarlierSlotPreference: 'Ky link nuk është më i vlefshëm për zëvendësim.',
        newSlotNotEarlier: 'Orari i ri duhet të jetë më i hershëm se rezervimi juaj aktual.',
        replacementAlreadyInProgress: 'Tashmë ka një kërkesë zëvendësimi në pritje për këtë rezervim.',
      },
    },
    dashboard: {
      reservationStatus: {
        all: 'Të gjitha',
        active: 'Aktive',
        pending: 'Në pritje',
        completed: 'Përfunduar',
        cancelled: 'Anuluar',
        rejected: 'Refuzuar',
      },
      calendar: {
        noServicesAssigned: 'Asnjë shërbim i caktuar',
        emailOptionalHint:
          'Email-i është opsional. Pa email, klienti nuk mund ta anulojë vetë rezervimin online.',
      },
      notifications: {
        newBooking: 'Rezervim i ri!',
        bookingCancelled: 'Një rezervim u anulua.',
        generic: 'Aktivitet i ri në llogarinë tuaj.',
        title: 'Njoftimet',
        markAllRead: 'Shëno të gjitha si të lexuara',
        empty: 'Nuk ka njoftime të reja',
        viewAllBookings: 'Shiko të gjitha rezervimet →',
        toastNew: 'I ri',
        toastCancelled: 'Anuluar',
        toastUpdated: 'Përditësuar',
      },
      notificationSettings: {
        emailLabel: 'Email për njoftimet',
        emailPlaceholder: 'email@biznesi.com',
        modeAll: 'Të gjitha njoftimet',
        modeAllDesc: 'Merr email për çdo rezervim të ri dhe çdo ndryshim të rëndësishëm.',
        modeImportant: 'Vetëm të rëndësishmet',
        modeImportantDesc: 'Merr email vetëm për anulime dhe ndryshime të rëndësishme — jo për rezervime të reja.',
        modeNone: 'Asnjë email',
        modeNoneDesc: 'Mos merr asnjë email njoftimi.',
        alwaysActiveNote: 'Njoftimet në panel mbeten gjithmonë aktive, pavarësisht nga zgjedhja e email-it. Zgjedhja më sipër kontrollon vetëm dërgimin shtesë me email.',
        saveButton: 'Ruaj njoftimet',
        saved: '✓ U ruajt',
      },
      nav: {
        overview: 'Pasqyra',
        subscription: 'Abonimi',
        bookings: 'Rezervimet',
        schedule: 'Kalendari',
        waitinglist: 'Lista e pritjes',
        analytics: 'Analitika',
        services: 'Shërbimet',
        clients: 'Klientët',
        staff: 'Stafi',
        profile: 'Profili',
        settings: 'Cilësimet',
        sectionMain: 'Kryesorja',
        sectionManagement: 'Menaxhim',
        sectionAccount: 'Llogaria',
        signOut: 'Dil nga llogaria',
        publicPage: 'Faqja publike',
      },
      days: {
        monday: 'E Hënë', tuesday: 'E Martë', wednesday: 'E Mërkurë',
        thursday: 'E Enjte', friday: 'E Premte', saturday: 'E Shtunë', sunday: 'E Diel',
      },
      categories: {
        beautySalon: 'Sallon Bukurie',
        barber: 'Berber',
        dentalClinic: 'Klinikë Dentare',
        medicalClinic: 'Klinikë Mjekësore',
        spaWellness: 'Spa & Wellness',
        gymFitness: 'Palestër / Fitness',
        photographer: 'Fotograf',
        consultant: 'Konsulent',
        lawyer: 'Avokat',
        accountant: 'Kontabilist',
        aestheticCenter: 'Qendër estetike',
        nailArtist: 'Nail Artist',
        other: 'Tjetër',
      },
      header: {
        themeLight: 'Kalo në Light mode',
        themeDark: 'Kalo në Dark mode',
        themeLightLabel: 'Dritë',
        themeDarkLabel: 'Errët',
        copied: 'Kopjuar!',
        copyLink: 'Kopjo linkun',
      },
      overview: {
        welcomePrefix: 'Mirë se vjen,',
        businessFallback: 'Biznes',
        subtitle: 'Ja çfarë po ndodh sot',
        statTodayBookings: 'Rezervime sot',
        statTodaySub: 'ditën e sotme',
        statActiveBookings: 'Rezervime aktive',
        statActiveSub: 'të konfirmuara',
        statCompleted: 'Të përfunduara',
        statCompletedSub: 'shërbime të kryera',
        statRevenue: 'Të ardhura',
        statRevenueSub: 'nga rezervimet e kryera',
        chartLast7Days: 'Rezervimet — 7 ditët e fundit',
        todayBookingsTitle: 'Rezervimet e sotme',
        viewAll: 'Shiko të gjitha →',
        noBookingsToday: 'Nuk ka rezervime për sot',
        quickActionAddService: 'Shto shërbim të ri',
        quickActionManageHours: 'Menaxho oraret',
        quickActionViewPublic: 'Shiko profilin publik',
        trialEndedTitle: 'Prova juaj falas ka përfunduar.',
        trialEndingSoon: (days: number) => `Prova juaj falas e Rezervo Online përfundon pas ${days} ditësh.`,
        trialEndedBody: 'Shtoni të dhënat e pagesës për të vazhduar përdorimin e Rezervo Online.',
        trialEndingBody: 'Shtoni një mënyrë pagese për të vazhduar përdorimin e platformës pa ndërprerje.',
        addCardCta: 'Shto kartën →',
        trialInfoLabel: 'Prova falas:',
        trialInfoDaysLeft: (days: number) => `${days} ditë të mbetura`,
        trialInfoSuffix: '· Po përdorni provën falas 30-ditore të Rezervo Online.',
        viewSubscription: 'Shiko abonimin →',
      },
      bookings: {
        title: 'Rezervimet',
        totalCount: (n: number) => `${n} rezervime gjithsej`,
        searchPlaceholder: 'Kërko klient...',
        emptyTitle: 'Nuk u gjet asnjë rezervim.',
        confirmQuestion: 'Konfirmo?',
        confirmAction: 'Konfirmo',
        rejectAction: 'Refuzo',
        rejectConfirmQuestion: 'Refuzo?',
        editModalTitle: 'Ndrysho rezervimin',
        clientNameLabel: 'Emri i klientit *',
        emailLabel: 'Email',
        dateLabel: 'Data *',
        timeLabel: 'Ora *',
        serviceLabelOptional: 'Shërbimi (opsional — lëre bosh për të mbajtur aktualin)',
        keepCurrentService: '— Mbaj shërbimin aktual —',
        notesLabel: 'Shënime',
        saveChanges: 'Ruaj ndryshimet',
      },
      waitingList: {
        title: 'Lista e pritjes',
        summary: (total: number, waiting: number) => `${total} hyrje gjithsej · ${waiting} në pritje`,
        tabWaitingList: 'Lista e pritjes',
        tabEarlierSlots: 'Orar më herët',
        waitingListHint: 'Klientë që nuk kanë rezervim dhe presin disponueshmëri. Kur anulohet një rezervim, njoftohen automatikisht.',
        earlierSlotsHint: 'Klientë që kanë rezervim aktiv dhe dëshirojnë një orar më të hershëm nëse lirohet.',
        filterAll: 'Të gjitha',
        emptyWaitingList: 'Lista e pritjes është bosh.',
        emptyWaitingListFiltered: (label: string) => `Nuk ka hyrje me status "${label}".`,
        emptyWaitingListHint: 'Klientët mund të regjistrohen nga faqja publike kur nuk ka orare të lira.',
        emptyEarlierSlots: 'Nuk ka kërkesa për orar më herët.',
        emptyEarlierSlotsHint: 'Klientët zgjedhin këtë opsion pas konfirmimit të rezervimit.',
        registered: (date: string) => `Regjistruar: ${date}`,
        notifiedAt: (date: string) => `Njoftuar: ${date}`,
        rescheduledAt: (date: string) => `Riplanifikuar: ${date}`,
        currentBooking: (date: string, time: string) => `Rezervim aktual: ${date} · ${time}`,
        notifyAction: 'Njofto',
        bookedAction: 'Rezervoi',
        expiredAction: 'Skaduar',
        statusActive: 'Aktiv',
        statusInactive: 'Joaktiv',
        statusRescheduled: 'Riplanifikuar',
        statusNotified: 'Njoftuar',
        waitingStatus: { waiting: 'Në pritje', notified: 'Njoftuar', booked: 'Rezervoi', expired: 'Skaduar' },
      },
      tabError: {
        message: 'Nuk mundëm të ngarkojmë të dhënat.',
        retry: 'Provo përsëri',
      },
      clients: {
        title: 'Klientët',
        uniqueCount: (n: number) => `${n} klientë unikë`,
        emptyTitle: 'Nuk ka klientë ende.',
        emptyHint: 'Klientët shfaqen automatikisht pasi bëjnë rezervim.',
        emailAction: 'Email',
        lastBooking: (date: string) => `Rezervimi i fundit: ${date}`,
      },
      staff: {
        title: 'Stafi',
        activeCount: (n: number) => `${n} anëtarë aktivë`,
        addMember: '+ Shto anëtar',
        editMember: 'Ndrysho anëtarin',
        newMember: 'Anëtar i ri i stafit',
        nameLabel: 'Emri *',
        namePlaceholder: 'p.sh. Arta Koci',
        roleLabel: 'Roli *',
        rolePlaceholder: 'p.sh. Stiliste',
        emailLabel: 'Email',
        phoneLabel: 'Telefoni',
        colorLabel: 'Ngjyra',
        servicesPerformed: 'Shërbimet që kryen',
        weeklySchedule: 'Orari javor i stafit',
        configureBusinessHoursFirst: '⚠️ Konfiguro orarin e biznesit fillimisht.',
        working: 'Punon',
        onLeave: 'Pushim',
        businessClosedShort: 'Biznesi mbyllur',
        addFirstMember: '+ Shto anëtarin e parë',
        noStaffYet: 'Nuk ke anëtarë stafi ende.',
        active: 'Aktiv',
        inactive: 'Joaktiv',
      },
      schedule: {
        title: 'Kalendari',
        selectDate: 'Zgjidhni një datë',
        serviceLabel: 'Shërbimi',
        allServices: 'Të gjitha shërbimet',
        staffLabel: 'Stafi',
        allStaff: 'Të gjithë stafi',
        addBooking: '+ Shto rezervim',
        manage: '⚙ Menaxho',
        free: 'I lirë',
        booked: 'I zënë',
        onLeaveLegend: 'Leje',
        outsideHours: 'Jashtë orarit',
        closedLegend: 'Mbyllur',
        businessClosurePrefix: 'Mbyllje biznesi',
        businessClosedPeriod: 'Biznesi është mbyllur për këtë periudhë.',
        businessClosedDay: 'Biznesi është mbyllur këtë ditë.',
        noActiveStaff: 'Nuk ka staf aktiv',
        noActiveStaffForService: 'për këtë shërbim',
        notWorkingToday: 'Nuk punon këtë ditë',
        onLeaveFullDay: '⏸ Me leje gjithë ditën',
        slotsFree: (n: number) => `${n} lirë`,
        slotsBooked: (n: number) => `${n} zënë`,
        businessHours: 'Orari i biznesit',
        noSchedulesConfigured: 'Nuk ka orare të konfiguruara.',
        createDefaultSchedules: '+ Krijo oraret e paracaktuara',
        creating: 'Duke krijuar...',
        minIntervalSuffix: 'min interval',
        open: 'Hapur',
        closed: 'Mbyllur',
        addBookingModalTitle: '+ Shto rezervim',
        bookingCreatedSuccess: 'Rezervimi u krijua me sukses.',
        dateLabel: 'Data',
        timeLabel: 'Ora',
        selectService: 'Zgjidh shërbimin',
        selectStaffRequired: 'Zgjidh stafin (i detyrueshëm)',
        noStaff: 'Pa staf',
        clientNameLabel: 'Emri i klientit *',
        clientNamePlaceholder: 'Emri',
        phoneLabel: 'Telefoni',
        emailOptionalLabel: 'Email (opsionale)',
        manageModalTitle: 'Menaxho',
        businessClosureTab: 'Pushim Biznesi',
        staffLeaveTab: 'Leje Stafi',
        closureIntro: 'Bloko të gjithë biznesin për periudhën e zgjedhur. Pas kësaj periudhe, orari normal rikthehet automatikisht.',
        fromDate: 'Nga data *',
        toDate: 'Deri më datë *',
        reasonOptional: 'Arsyeja (opsionale)',
        closureReasonPlaceholder: 'p.sh. Pushime vjetore',
        leaveReasonPlaceholder: 'p.sh. Pushim mjekësor',
        leaveIntro: 'Shto leje për një anëtar stafi. Pas kësaj date, orari normal rikthehet automatikisht.',
        staffMemberLabel: 'Anëtari i stafit *',
        selectMember: 'Zgjidh anëtarin',
        leaveDateLabel: 'Data e lejes *',
        fullDay: 'Ditë e plotë',
        specificHours: 'Orë specifike',
        fromTime: 'Nga ora',
        toTime: 'Deri në orë',
        conflictsWarning: (n: number) => `⚠️ ${n} rezervim(e) ekzistues(e) preken:`,
        confirmSaveAgain: 'Kliko "Ruaj" sërish për të konfirmuar.',
        confirmAndSave: 'Konfirmo dhe Ruaj',
        existing: 'Ekzistuese',
        staffRequiredError: 'Zgjidhni anëtarin e stafit.',
        savedChanges: 'Ndryshimet u ruajtën.',
        endDateBeforeStartError: 'Data e mbarimit nuk mund të jetë para datës së fillimit.',
        allDatesAlreadyHaveLeave: 'Të gjitha datat e zgjedhura tashmë kanë leje për këtë anëtar.',
        savedWithSkippedDates: (saved: number, skipped: number) =>
          `U ruajt leja për ${saved} ditë; ${skipped} ditë tashmë kishin leje dhe u anashkaluan.`,
      },
      services: {
        title: 'Shërbimet',
        count: (n: number) => `${n} shërbime`,
        addService: '+ Shto shërbim',
        editService: 'Ndrysho shërbimin',
        newService: 'Shërbim i ri',
        nameLabel: 'Emri i shërbimit *',
        namePlaceholder: 'p.sh. Prerje flokësh',
        priceLabel: 'Çmimi (€)',
        optionalSuffix: '(opsional)',
        pricePlaceholder: 'p.sh. 15',
        durationLabel: 'Kohëzgjatja (min) *',
        descriptionLabel: 'Përshkrimi (opsional)',
        descriptionPlaceholder: 'Përshkrim i shkurtër...',
        addServiceBtn: 'Shto shërbimin',
        noServicesYet: 'Nuk ke shërbime ende.',
        addFirstService: '+ Shto shërbimin e parë',
        active: 'Aktiv',
        inactive: 'Joaktiv',
      },
      profile: {
        title: 'Profili i biznesit',
        subtitle: 'Informacioni që shohin klientët tuaj',
        photoLabel: 'Foto e profilit / Logo',
        uploadPhoto: 'Ngarko foton',
        changePhoto: 'Ndrysho foton',
        removePhoto: 'Hiq foton',
        photoHint: 'JPG, PNG ose WebP · Maksimumi 5MB',
        nameLabel: 'Emri i biznesit',
        namePlaceholder: 'Studio Elegance',
        categoryLabel: 'Kategoria',
        selectCategory: 'Zgjidh kategorinë',
        phoneLabel: 'Telefoni',
        websiteLabel: 'Website',
        addressLabel: 'Adresa',
        addressPlaceholder: 'Rruga, Nr.',
        cityLabel: 'Qyteti',
        cityPlaceholder: 'Tiranë',
        descriptionLabel: 'Përshkrimi',
        descriptionPlaceholder: 'Përshkruaj biznesin tënd...',
        saveProfile: 'Ruaj profilin',
        saved: '✓ Ruajtur!',
        savedSuccess: 'Ndryshimet u ruajtën me sukses!',
        publicPreview: 'Pamja publike',
        businessNameFallback: 'Emri i biznesit',
        bookingLink: 'Linku i rezervimit:',
        copy: 'Kopjo',
      },
      settings: {
        title: 'Cilësimet',
        subtitle: 'Preferencat e llogarisë dhe biznesit',
        account: 'Llogaria',
        businessFallback: 'Biznesi im',
        appearance: 'Pamja e dashboard-it',
        displayMode: 'Mënyra e shfaqjes',
        homepageAlwaysDark: 'Faqja kryesore mbetet gjithmonë Dark',
        notifications: 'Njoftimet',
        autoConfirm: 'Konfirmim automatik',
        autoConfirmOnDesc: 'Rezervimet e reja konfirmohen automatikisht.',
        autoConfirmOffDesc: 'Rezervimet e reja mbeten "Në pritje" derisa t\'i konfirmoni ose refuzoni ju vetë.',
        bookingLinkTitle: 'Linku i rezervimit',
        bookingLinkDesc: 'Ndaj këtë link me klientët tuaj për të marrë rezervime online.',
        copied: '✓ Kopjuar',
        viewPublicPage: '↗ Shiko faqen publike',
        dangerZone: 'Zona e rrezikshme',
        irreversibleActions: 'Veprimet e mëposhtme janë të pakthyeshme.',
        signOut: 'Dil nga llogaria',
      },
      subscription: {
        title: 'Abonimi',
        subtitle: 'Menaxho abonimin tuaj të Rezervo Online',
        statusActive: 'Aboniment aktiv',
        statusTrial: 'Provë falas 30-ditore',
        statusPastDue: 'Pagesa e vonuar',
        statusCancelled: 'Aboniment i anuluar',
        statusPaymentRequired: 'Kërkohet pagesa',
        daysLeftFromTrial: (n: number) => `${n} ditë të mbetura nga prova falas`,
        trialEnded: 'Prova falas ka përfunduar',
        fullyActive: 'Platforma është plotësisht aktive',
        addPaymentToContinue: 'Shtoni një mënyrë pagese për të vazhduar',
        badgeTrial: 'Trial',
        badgeActive: 'Aktiv',
        badgePastDue: 'Past Due',
        badgeCancelled: 'Anuluar',
        badgePaymentRequired: 'Kërkohet pagesa',
        trialProgress: 'Progresi i provës',
        daysOf30: (n: number) => `${n}/30 ditë`,
        started: 'Filloi',
        ends: 'Mbaron',
        trialBodyText: 'Po përdorni provën falas 30-ditore të Rezervo Online. Mund të shtoni një mënyrë pagese në çdo kohë gjatë provës — nuk kërkohet gjatë regjistrimit.',
        activeBodyText: 'Abonimenti juaj është aktiv. Keni akses të plotë në të gjitha funksionalitetet e platformës.',
        inactiveBodyText: 'Prova juaj falas ka përfunduar. Shtoni të dhënat e pagesës për të rifituar aksesin e plotë. Të gjitha të dhënat tuaja janë ruajtur.',
        activatedSuccess: 'Abonimi u aktivizua me sukses!',
        paypalConfirmingText: 'Pagesa juaj u pranua nga PayPal. Statusi i abonimit do të përditësohet automatikisht pas verifikimit nga sistemi.',
        activateWithPaypal: '💳 Aktivizo abonimin me PayPal',
        priceLine: (price: string) => `${price} EUR / muaj · Rinovim automatik · Anulo kur të dëshirosh`,
        retryLoadingPaypal: '🔄 Provo sërish',
        loadingPaypal: 'Duke ngarkuar PayPal...',
        paypalGenericError: 'Pagesa dështoi. Provoni sërish ose kontaktoni mbështetjen.',
        paypalCancelledError: 'Pagesa u anulua ose dritarja u mbyll para aprovimit. Klikoni "Provo sërish" nëse dëshironi të vazhdoni.',
        paypalSecureNote: '🔒 Pagesa procesohet në mënyrë të sigurt nga PayPal Sandbox. Të dhënat e kartës nuk ruhen nga Rezervo Online.',
        supabaseConfigMissing: 'Konfigurimi i Supabase mungon.',
        paypalConfigMissing: 'Konfigurimi i PayPal mungon. Kontaktoni mbështetjen.',
        paypalLoadError: 'Gabim gjatë ngarkimit të PayPal. Provoni sërish.',
        whatsIncluded: 'Çfarë përfshin Rezervo Online',
        features: [
          'Rezervime të pakufizuara online',
          'Dashboard i plotë me analitika',
          'Menaxhim i stafit dhe shërbimeve',
          'Lista e pritjes automatike',
          'Njoftimet me email',
          'Faqe publike e personalizuar',
        ],
      },
      analytics: {
        title: 'Analitika',
        subtitle: 'Statistikat e rezervimeve dhe të ardhurave',
        period7d: '7 ditë',
        period30d: '30 ditë',
        period90d: '90 ditë',
        totalBookings: 'Rezervime totale',
        revenue: 'Të ardhura',
        completionRate: 'Shkalla e përfundimit',
        uniqueClients: 'Klientë unikë',
        dailyBookings: 'Rezervimet ditore',
        bookingStatus: 'Statusi i rezervimeve',
        noData: 'Nuk ka të dhëna',
        dailyRevenue: 'Të ardhurat ditore (€)',
        topServices: 'Shërbimet kryesore',
        chartBookingsLabel: 'Rezervime',
        chartRevenueLabel: 'Të ardhura',
      },
      errors: {
        saveFailed: 'Ruajtja dështoi.',
        loadFailed: 'Ngarkimi i të dhënave dështoi.',
        uploadFailed: 'Ngarkimi dështoi.',
        createFailed: 'Krijimi dështoi.',
        photoTooLarge: 'Fotoja nuk duhet të kalojë 5MB.',
        nameRequired: 'Emri i anëtarit është i detyrueshëm.',
        roleRequired: 'Roli është i detyrueshëm.',
        staffSaveFailed: 'Ruajtja dështoi. Provo përsëri.',
        businessClosedAt: (day: string) => `Biznesi është mbyllur të ${day}`,
        openBeforeClose: 'Ora e fillimit duhet të jetë para orës së mbarimit',
        staffHoursWithinBusiness: (open: string, close: string) => `Orari i stafit duhet të jetë brenda ${open}–${close}`,
      },
      addBookingErrors: {
        clientNameRequired: 'Emri i klientit është i detyrueshëm.',
        businessNotFound: 'Biznesi nuk u gjet.',
        serviceNotFound: 'Shërbimi nuk u gjet ose nuk është aktiv.',
        dateInPast: 'Nuk mund të rezervoni në një datë të kaluar.',
        slotInPast: 'Kjo orë ka kaluar tashmë sot. Zgjidhni një orë tjetër.',
        businessClosed: 'Biznesi është i mbyllur për këtë periudhë.',
        businessClosedThatDay: 'Biznesi është i mbyllur këtë ditë.',
        outsideBusinessHours: 'Ora e zgjedhur është jashtë orarit të punës.',
        staffRequired: 'Zgjidhni një anëtar stafi.',
        staffNotFound: 'Stafi i zgjedhur nuk u gjet.',
        staffNotEligible: 'Ky staf nuk mund ta kryejë këtë shërbim.',
        noAvailability: 'Ky orar është zënë. Zgjidhni një orar tjetër.',
        notAuthorized: 'Nuk keni akses për këtë veprim.',
        generic: 'Ndodhi një gabim. Provo përsëri.',
        fillRequiredFields: 'Plotësoni datën, orën dhe shërbimin.',
      },
    },
    legal: {
      lastUpdated: 'Përditësuar më 1 Shtator 2026',
      footerTerms: 'Kushtet',
      footerPrivacy: 'Privatësia',
      contactHeading: 'Kontakt',
      contactIntro: 'Për pyetje rreth kësaj faqeje, na kontaktoni në:',
      bookingNotice: {
        prefix: 'Duke vazhduar me rezervimin, të dhënat tuaja do të përpunohen për administrimin e rezervimit. Shiko ',
        linkText: 'Politikën e Privatësisë',
        suffix: '.',
      },
      registerNotice: {
        prefix: 'Duke krijuar llogarinë, ju pranoni ',
        termsLink: 'Kushtet',
        middle: ' dhe konfirmoni se keni lexuar ',
        privacyLink: 'Politikën e Privatësisë',
        suffix: '.',
      },
      terms: {
        title: 'Kushtet e Përdorimit',
        intro: 'Këto kushte rregullojnë përdorimin e platformës Rezervo Online nga bizneset që e përdorin atë.',
        sections: {
          about: {
            heading: 'Rreth Shërbimit',
            body: [
              'Rezervo Online është një platformë SaaS (softuer si shërbim) për menaxhimin e rezervimeve online, e destinuar për biznese.',
              'Bizneset krijojnë dhe menaxhojnë vetë shërbimet, stafin, oraret dhe rezervimet e tyre përmes platformës.',
              'Rezervo Online nuk është ofruesi i shërbimit nënkuptues (bukurie, mjekësor, profesional, etj.) që rezervohet nga klienti fundor — ai shërbim ofrohet nga biznesi që ka krijuar rezervimin.',
            ],
          },
          responsibilities: {
            heading: 'Përgjegjësia e Biznesit',
            body: [
              'Biznesi përdorues është përgjegjës për saktësinë e informacionit të tij, disponueshmërinë, shërbimet e ofruara, dhe detyrimet ndaj klientëve të vet.',
            ],
          },
          subscription: {
            heading: 'Abonimi dhe Pagesat',
            body: [
              'Ofrojmë 30 ditë periudhë prove falas.',
              'Çmimi i abonimit me pagesë është 27€/muaj.',
              'Pas aktivizimit, abonimi rinovohet automatikisht çdo muaj derisa të anulohet.',
              'Pagesat përpunohen nga PayPal. Rezervo Online nuk ruan të dhëna të papërpunuara karte/pagese.',
              'Nëse abonimi ose periudha e provës nuk është aktive, pranimi i rezervimeve të reja online mund të çaktivizohet përkohësisht, ndërsa aksesi në llogari/faturim mund të mbetet i disponueshëm.',
              'Përdoruesi mund ta anulojë abonimin në çdo kohë. Anulimi ndalon rinovimin e ardhshëm; aksesi vazhdon sipas periudhës tashmë të paguar, siç funksionon momentalisht produkti.',
              'Shumat tashmë të paguara përgjithësisht nuk kthehen, përveçse kur kërkohet nga ligji në fuqi ose kur është rënë dakord shprehimisht ndryshe.',
            ],
          },
          acceptableUse: {
            heading: 'Përdorimi i Lejuar',
            body: [
              'Platforma duhet të përdoret vetëm për qëllime të ligjshme biznesi. Nuk lejohet përdorimi abuziv, mashtrues, ose që dëmton funksionimin e platformës apo përdoruesit e tjerë.',
            ],
          },
          accountSecurity: {
            heading: 'Siguria e Llogarisë',
            body: [
              'Përdoruesi është përgjegjës për ruajtjen e konfidencialitetit të kredencialeve të llogarisë së tij dhe për çdo veprimtari që ndodh nën atë llogari.',
            ],
          },
          availability: {
            heading: 'Disponueshmëria dhe Ndërprerjet',
            body: [
              'Bëjmë përpjekje të arsyeshme që platforma të jetë e disponueshme, por nuk garantojmë funksionim pa ndërprerje. Mund të ndodhin mirëmbajtje ose ndërprerje të përkohshme teknike.',
            ],
          },
          liability: {
            heading: 'Kufizimi i Përgjegjësisë',
            body: [
              'Në masën maksimale të lejuar nga ligji, Rezervo Online nuk mban përgjegjësi për dëme indirekte, të rastësishme ose pasuese që rrjedhin nga përdorimi i platformës, përfshirë marrëdhënien mes një biznesi dhe klientëve të tij.',
            ],
          },
          termination: {
            heading: 'Ndërprerja',
            body: [
              'Mund të pezullojmë ose mbyllim aksesin në rast shkeljeje të këtyre kushteve. Përdoruesi mund të mbyllë llogarinë e vet në çdo kohë.',
            ],
          },
          changes: {
            heading: 'Ndryshimet e Kushteve',
            body: [
              'Këto kushte mund të përditësohen herë pas here. Vazhdimi i përdorimit të platformës pas ndryshimeve nënkupton pranimin e tyre.',
            ],
          },
          governingLaw: {
            heading: 'Ligji i Zbatueshëm',
            body: [
              'Këto kushte rregullohen nga ligjet e Republikës së Shqipërisë.',
            ],
          },
        },
      },
      privacy: {
        title: 'Politika e Privatësisë',
        intro: 'Kjo politikë shpjegon si Rezervo Online mbledh, përdor dhe mbron të dhënat personale, në përputhje me Ligjin Nr. 124/2024 "Për Mbrojtjen e të Dhënave Personale".',
        sections: {
          dataCollected: {
            heading: 'Të Dhënat që Përpunojmë',
            body: [
              'Të dhëna të llogarisë së biznesit: emër, email, telefon, informacion profili/biznesi, dhe informacion mbi statusin e abonimit/pagesës.',
              'Të dhëna të klientit që rezervon: emër, email, telefon, data/ora e rezervimit, shërbimi i zgjedhur, dhe stafi i zgjedhur (kur aplikohet).',
            ],
          },
          purposes: {
            heading: 'Qëllimet e Përpunimit',
            body: [
              'I përdorim këto të dhëna për: operimin e llogarisë, menaxhimin e rezervimeve, dërgimin e email-eve transaksionale, njoftime, siguri, administrimin e faturimit/abonimit, dhe mbështetje ndaj klientit.',
            ],
          },
          lawfulBasis: {
            heading: 'Baza Ligjore e Përpunimit',
            body: [
              'Përpunojmë të dhëna vetëm kur është e nevojshme për ofrimin e shërbimit (ekzekutim kontrate), për të përmbushur detyrime ligjore, ose në bazë të interesit tonë legjitim për sigurinë dhe funksionimin e platformës.',
            ],
          },
          controllerProcessor: {
            heading: 'Roli si Kontrollues / Përpunues',
            body: [
              'Rezervo Online vepron si kontrollues për të dhënat e llogarisë së biznesit, abonimit, sigurisë dhe administrimit të shërbimit.',
              'Për të dhënat e klientëve që një biznes përpunon përmes platformës, biznesi përcakton qëllimin e rezervimit/shërbimit, ndërsa Rezervo Online i përpunon këto të dhëna për të mundësuar funksionimin e platformës.',
            ],
          },
          thirdParties: {
            heading: 'Ofrues të Shërbimeve të Palëve të Treta',
            body: [
              'Përdorim ofrues të besuar për të mundësuar shërbimin: Supabase (bazë të dhënash/autentikim/backend), Resend (dërgim email-esh transaksionale), PayPal (përpunim abonimi/pagese), dhe Netlify (hosting i faqes).',
              'Të dhënat e papërpunuara të kartës/pagesës trajtohen drejtpërdrejt nga ofruesi i pagesave dhe nuk ruhen nga Rezervo Online.',
            ],
          },
          dataSharing: {
            heading: 'Ndarja e të Dhënave',
            body: [
              'Nuk shesim të dhëna personale. I ndajmë të dhënat vetëm me ofruesit e shërbimit të përmendur më sipër, në masën e nevojshme për funksionimin e platformës, ose kur kërkohet nga ligji.',
            ],
          },
          international: {
            heading: 'Përpunimi nga Ofrues Ndërkombëtarë',
            body: [
              'Disa nga ofruesit tanë të shërbimit mund të përpunojnë të dhëna jashtë Shqipërisë. Në këto raste synojmë të sigurohemi që përpunimi të bëhet nga ofrues me masa mbrojtëse të përshtatshme.',
            ],
          },
          security: {
            heading: 'Siguria',
            body: [
              'Marrim masa të arsyeshme teknike dhe organizative për të mbrojtur të dhënat personale. Asnjë sistem nuk mund të garantojë siguri absolute.',
            ],
          },
          retention: {
            heading: 'Ruajtja e të Dhënave',
            body: [
              'Ruajmë të dhënat për aq kohë sa është e nevojshme për qëllimet e përshkruara më sipër, ose siç kërkohet nga ligji.',
            ],
          },
          rights: {
            heading: 'Të Drejtat Tuaja',
            body: [
              'Në përputhje me ligjin në fuqi, keni të drejtë të kërkoni akses, korrigjim, fshirje, ose kufizim të përpunimit të të dhënave tuaja personale, si dhe të drejta të tjera të parashikuara nga Ligji Nr. 124/2024. Për t\'i ushtruar këto të drejta, na kontaktoni në adresën më poshtë.',
            ],
          },
          cookies: {
            heading: 'Cookies dhe Ruajtja Teknike',
            body: [
              'Përdorim ruajtje teknike lokale (si cookies/localStorage) vetëm për funksione thelbësore, si autentikimi dhe ruajtja e preferencave (p.sh. gjuha e faqes).',
            ],
          },
          changes: {
            heading: 'Ndryshimet e Politikës',
            body: [
              'Kjo politikë mund të përditësohet herë pas here. Ndryshimet e rëndësishme do të pasqyrohen në këtë faqe.',
            ],
          },
        },
      },
    },
    register: {
      tagline: '30 ditë falas • Pa kartë krediti',
      stepLabels: ['Llogaria', 'Informacioni', 'Orari'],
      alreadyHaveAccount: 'Ke tashmë llogari?',
      loginHere: 'Hyr këtu',
      continueCta: 'Vazhdo →',
      savingCta: 'Duke ruajtur...',
      step1: {
        title: 'Regjistro biznesin tënd',
        subtitle: 'Hapi 1 nga 3 — Llogaria',
        businessNameLabel: 'Emri i biznesit *',
        businessNamePlaceholder: 'p.sh. Studio Elegance',
        emailLabel: 'Email *',
        emailPlaceholder: 'email@biznesi.al',
        phoneLabel: 'Numri i telefonit',
        phonePlaceholder: '+355 69 000 0000',
        passwordLabel: 'Fjalëkalimi *',
        passwordPlaceholder: 'Minimum 6 karaktere',
        creatingAccountCta: 'Duke krijuar llogarinë...',
      },
      step2: {
        title: 'Detajet e biznesit',
        subtitle: 'Hapi 2 nga 3 — Informacioni i biznesit',
        categoryLabel: 'Kategoria e biznesit *',
        categoryPlaceholder: 'Zgjidhni kategorinë...',
        cityLabel: 'Qyteti *',
        cityPlaceholder: 'p.sh. Tiranë',
        addressLabel: 'Adresa',
        addressPlaceholder: 'p.sh. Rruga Myslym Shyri, Nr. 5',
        descriptionLabel: 'Përshkrimi (opsional)',
        descriptionPlaceholder: 'Përshkruani biznesin tuaj shkurtimisht...',
      },
      step3: {
        title: 'Orari i biznesit',
        subtitle: 'Hapi 3 nga 3 — Orari i përgjithshëm i biznesit',
        bookingIntervalLabel: 'Intervali global i rezervimeve',
        bookingIntervalHelp: 'Kontrollon oraret e mundshme të fillimit të rezervimeve (p.sh. 30 min → 09:00, 09:30, 10:00...)',
        weeklyScheduleLabel: 'Orari javor',
        closedLabel: 'Mbyllur',
        finishCta: 'Përfundo regjistrimin →',
      },
      errors: {
        businessNameRequired: 'Emri i biznesit është i detyrueshëm.',
        emailRequired: 'Email-i është i detyrueshëm.',
        passwordTooShort: 'Fjalëkalimi duhet të ketë të paktën 6 karaktere.',
        categoryRequired: 'Ju lutem zgjidhni kategorinë e biznesit.',
        cityRequired: 'Qyteti është i detyrueshëm.',
        businessIdMissing: 'Gabim: ID e biznesit mungon.',
        generic: 'Ndodhi një gabim. Provo përsëri.',
        authenticationRequired: 'Sesioni juaj ka skaduar. Ju lutemi provoni sërish.',
        businessAlreadyExists: 'Kjo llogari ka tashmë një biznes të regjistruar.',
        slugGenerationFailed: 'Ndodhi një gabim gjatë regjistrimit. Ju lutemi provoni sërish.',
      },
    },
    admin: {
      nav: { overview: 'Përmbledhje', businesses: 'Bizneset' },
      header: { title: 'Rezervo Admin', badge: 'ADMINISTRIM I PLATFORMËS', backToDashboard: 'Kthehu te paneli' },
      accessDenied: {
        title: 'Akses i kufizuar',
        body: 'Nuk keni akses në panelin e administrimit të platformës.',
        backToDashboard: 'Kthehu te paneli im',
      },
      loading: 'Duke ngarkuar...',
      loadError: 'Ndodhi një gabim gjatë ngarkimit. Provo përsëri.',
      overview: {
        totalBusinesses: 'Bizneset gjithsej',
        testBusinesses: 'Bizneset e testimit',
        trialBusinesses: 'Në periudhë prove',
        activeBusinesses: 'Aktive / Me pagesë',
        expiredBlockedBusinesses: 'Skaduara / Bllokuara',
        totalBookings: 'Rezervimet gjithsej',
        totalCustomers: 'Klientët gjithsej',
        totalServices: 'Shërbimet gjithsej',
        totalStaff: 'Stafi gjithsej',
        bookingsToday: 'Rezervime sot',
        bookingsLast7Days: 'Rezervime (7 ditët e fundit)',
        newBusinessesLast30Days: 'Biznese të reja (30 ditët e fundit)',
      },
      businesses: {
        title: 'Bizneset',
        searchPlaceholder: 'Kërko sipas emrit të biznesit ose email-it të pronarit...',
        filterAll: 'Të gjitha',
        filterTrial: 'Në provë',
        filterActive: 'Aktive',
        filterExpiredBlocked: 'Skaduara/Bllokuara',
        filterTest: 'Testim',
        categoryAll: 'Të gjitha kategoritë',
        columns: {
          name: 'Biznesi',
          owner: 'Pronari',
          phone: 'Telefoni',
          category: 'Kategoria',
          city: 'Qyteti',
          created: 'Krijuar',
          trialEnd: 'Fundi i provës',
          status: 'Statusi',
          bookingStatus: 'Rezervimet',
          paypal: 'PayPal',
          test: 'Test',
          services: 'Shërbime',
          staff: 'Staf',
          bookings: 'Rezervime',
          customers: 'Klientë',
        },
        empty: 'Asnjë biznes nuk përputhet me kriteret e kërkimit.',
        prev: '← Mbrapa',
        next: 'Përpara →',
        pageInfo: (from: number, to: number, total: number) => `${from}–${to} nga ${total}`,
        viewDetail: 'Shiko detajet',
        none: '—',
      },
      status: { trial: 'Provë', active: 'Aktiv', expiredBlocked: 'Bllokuar', test: 'Test', enabled: 'Aktiv', blocked: 'Bllokuar' },
      detail: {
        title: 'Detajet e biznesit',
        basic: 'Të përgjithshme',
        subscription: 'Abonimi',
        usage: 'Përdorimi',
        slug: 'Slug',
        owner: 'Email i pronarit',
        phone: 'Telefoni',
        category: 'Kategoria',
        city: 'Qyteti',
        created: 'Krijuar',
        website: 'Website',
        none: '—',
        trialStart: 'Fillimi i provës',
        trialEnd: 'Fundi i provës',
        status: 'Statusi i abonimit',
        bookingStatus: 'Statusi i rezervimeve',
        paypal: 'ID e abonimit PayPal',
        testFlag: 'Biznes testimi',
        totalBookings: 'Rezervime gjithsej',
        bookingsLast30Days: 'Rezervime (30 ditët e fundit)',
        clients: 'Klientë (unikë)',
        services: 'Shërbime',
        staff: 'Staf',
        waitingList: 'Në listën e pritjes',
        lastBooking: 'Rezervimi i fundit',
        never: 'Asnjëherë',
        openPublicPage: 'Hap faqen publike →',
        close: 'Mbyll',
      },
    },
    pwa: {
      dashboardCard: {
        title: 'Instalo Rezervo',
        body: 'Hape panelin direkt nga ekrani kryesor i telefonit.',
        androidCta: 'Instalo Rezervo',
        iosCta: 'Shto në ekranin kryesor',
        installedLabel: '✓ Rezervo është instaluar',
      },
      iosSheet: {
        title: 'Instalo Rezervo në iPhone',
        step1: 'Prek Share ⬆️ në Safari',
        step2: 'Zgjidh "Add to Home Screen"',
        step3: 'Prek "Add"',
        footer: 'Pastaj hape Rezervo direkt nga Home Screen.',
      },
      registerSuccess: {
        heading: 'Rezervo është gati 🎉',
        body: 'Shto Rezervo në telefon për ta hapur panelin me një prekje.',
        continueCta: 'Vazhdo te Dashboard',
      },
    },
  },
  en: {
    common: {
      loading: 'Loading...',
      save: 'Save',
      saving: 'Saving...',
      cancel: 'Cancel',
      yes: 'Yes',
      no: 'No',
      close: 'Close',
      back: 'Back',
      continue: 'Continue',
      confirm: 'Confirm',
      error: 'Something went wrong.',
      success: 'Done successfully.',
      edit: 'Edit',
      delete: 'Delete',
      add: 'Add',
      search: 'Search',
    },
    booking: {
      brandSuffix: 'Online',
      poweredBy: 'Powered by',
      loading: 'Loading...',
      notFoundTitle: 'Business not found',
      notFoundBody: 'This link may be wrong, or the business is not active.',
      backToHome: 'Back to homepage',

      weekdaysShort: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
      months: [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
      ],

      steps: {
        service: 'Service',
        datetime: 'Date & Time',
        info: 'Your details',
      },

      service: {
        title: 'Choose a service',
        subtitle: 'What service would you like?',
        empty: 'No services available.',
        durationMinutes: (n: number) => `${n} minutes`,
        cancelExisting: 'Cancel an existing booking',
      },

      datetime: {
        title: 'Choose date and time',
        durationShort: (n: number) => `${n} min`,
        availableLegend: 'Available',
        bookedLegend: 'Booked',
        noSlotsTitle: 'No open slots right now.',
        noSlotsQuestion: 'Would you like us to notify you if a slot opens up?',
        notifyMe: 'Yes, notify me',
        noThanks: 'No, thanks',
        fullNameLabel: 'Full name',
        fullNamePlaceholder: 'Arta Kelmendi',
        emailLabel: 'Email',
        emailPlaceholder: 'arta@email.com',
        phoneOptionalLabel: 'Phone (optional)',
        phonePlaceholder: '+355 69 123 4567',
        addToWaitlist: 'Join the waitlist',
        savingEllipsis: 'Saving...',
        waitlistSuccessTitle: 'You’ve been added to the waitlist.',
        waitlistSuccessBody: 'We’ll notify you as soon as a slot opens up.',
        availableTimesLabel: 'Available times',
        noSlotsForDay: 'No available times for this day.',
        continueCta: 'Continue',
      },

      info: {
        title: 'Your details',
        phoneLabel: 'Phone number',
        notesOptionalLabel: 'Notes (optional)',
        notesPlaceholder: 'Any special requests...',
        confirmCta: 'Confirm booking',
        sendingEllipsis: 'Sending...',
      },

      cancelModal: {
        title: 'Cancel booking',
        intro:
          'Enter your Booking ID (from the email or confirmation screen) and your email to find your booking.',
        bookingIdLabel: 'Booking ID',
        bookingIdPlaceholder: 'e.g. RZV-AB12CD',
        yourEmailLabel: 'Your email',
        findBooking: 'Find booking',
        searchingEllipsis: 'Searching...',
        notFound: 'Booking not found. Check the ID and email.',
        genericError: 'Something went wrong. Please try again.',
        cancelFailed: 'Cancellation failed. Please try again.',
        confirmHeading: 'Confirm cancellation',
        clientLabel: 'Client:',
        serviceLabel: 'Service:',
        dateLabel: 'Date:',
        irreversibleWarning: 'This action cannot be undone. Are you sure?',
        confirmCancelYes: 'Yes, cancel booking',
        cancellingEllipsis: 'Cancelling...',
        cancelSuccessTitle: 'Booking cancelled successfully.',
        cancelSuccessBody: 'The slot has been freed and can be booked by other clients.',
      },

      tokenCancel: {
        invalid: 'This cancellation link is not valid.',
        backToBooking: 'Back to the booking page',
        cancelledTitle: 'Booking cancelled.',
        cancelledBody: 'The slot has been freed.',
        bookAgain: 'Make another booking',
        title: 'Cancel booking',
        irreversibleWarning: 'This action cannot be undone. Are you sure?',
        confirmCancelYes: 'Yes, cancel booking',
        cancellingEllipsis: 'Cancelling...',
        cancelFailed: 'Cancellation failed. Please try again.',
      },

      earlierSlot: {
        confirmedTitle: 'Your booking is confirmed.',
        question: 'Would you like us to notify you if an earlier slot becomes available?',
        yesNotifyMe: 'Yes, notify me',
        savingEllipsis: 'Saving...',
        done: 'We’ll notify you if an earlier slot becomes available!',
        replacementNotice: 'You’re choosing an earlier slot. Your current booking will only be cancelled once the new time is confirmed.',
        verifyIntro: 'You’re choosing an earlier time for your current booking.',
        verifyEmailPrompt: 'Confirm the email on this booking to continue.',
        verifyButton: 'Continue',
        verifyingEllipsis: 'Verifying...',
      },

      confirmation: {
        confirmedTitle: 'Booking confirmed!',
        pendingTitle: 'Booking request sent',
        bookingIdLabel: 'Booking ID:',
        dateLabel: 'Date',
        timeLabel: 'Time',
        clientLabel: 'Client',
        emailLabel: 'Email',
        phoneLabel: 'Phone',
        addToCalendar: 'Add to Calendar',
        googleCalendar: 'Google Calendar',
        share: 'Share with others',
        linkCopied: 'Copied!',
        copyLink: 'Copy link',
        shareAction: 'Share',
        confirmedBannerTitle: 'Booking confirmed!',
        confirmedBannerBody: 'Your booking is confirmed. See you on the day!',
        pendingBannerTitle: 'Awaiting confirmation',
        pendingBannerBody: 'The business will review and confirm your booking soon. We\'ll email you when they do.',
        bookAgain: 'Make another booking',
        homePage: 'Homepage',
        cancelThisBooking: 'Cancel this booking',
        earlierSlotQuestion:
          'Would you like to be notified if an earlier appointment becomes available?',
      },

      errors: {
        noAvailability: 'This slot was just taken by someone else. Please choose another time.',
        businessClosed: 'The business is closed on this date.',
        businessClosedThatDay: 'The business is closed on this day.',
        outsideBusinessHours: 'The selected time is outside business hours.',
        dateInPast: 'You cannot book a date in the past.',
        slotInPast: 'This time is no longer available. Please choose another time.',
        businessNotFound: 'Business not found.',
        serviceNotFound: 'Service not found or no longer active.',
        clientNameRequired: 'Name is required.',
        clientEmailRequired: 'Email is required.',
        clientPhoneRequired: 'Please enter your phone number.',
        bookingsUnavailable: 'Online bookings are currently unavailable.',
        generic: 'Something went wrong. Please try again.',
        bookingNotFound: 'We couldn’t find your booking. Please check the code and email.',
        originalBookingNotActive: 'Your current booking is no longer active.',
        originalBookingNotInFuture: 'Your current booking has already passed.',
        noEligibleEarlierSlotPreference: 'This link is no longer valid for a replacement.',
        newSlotNotEarlier: 'The new time must be earlier than your current booking.',
        replacementAlreadyInProgress: 'A replacement request is already pending for this booking.',
      },
    },
    dashboard: {
      reservationStatus: {
        all: 'All',
        active: 'Active',
        pending: 'Pending',
        completed: 'Completed',
        cancelled: 'Cancelled',
        rejected: 'Rejected',
      },
      calendar: {
        noServicesAssigned: 'No services assigned',
        emailOptionalHint:
          'Email is optional. Without an email, the customer cannot cancel the booking online themselves.',
      },
      notifications: {
        newBooking: 'New booking!',
        bookingCancelled: 'A booking was cancelled.',
        generic: 'New activity on your account.',
        title: 'Notifications',
        markAllRead: 'Mark all as read',
        empty: 'No new notifications',
        viewAllBookings: 'View all bookings →',
        toastNew: 'New',
        toastCancelled: 'Cancelled',
        toastUpdated: 'Updated',
      },
      notificationSettings: {
        emailLabel: 'Notification email',
        emailPlaceholder: 'email@business.com',
        modeAll: 'All notifications',
        modeAllDesc: 'Get an email for every new booking and every important update.',
        modeImportant: 'Important only',
        modeImportantDesc: 'Get an email only for cancellations and important updates — not for new bookings.',
        modeNone: 'No email',
        modeNoneDesc: 'Do not receive any notification emails.',
        alwaysActiveNote: 'In-app notifications remain active regardless of your email choice. The selection above controls only additional email delivery.',
        saveButton: 'Save notifications',
        saved: '✓ Saved',
      },
      nav: {
        overview: 'Overview',
        subscription: 'Subscription',
        bookings: 'Bookings',
        schedule: 'Calendar',
        waitinglist: 'Waiting List',
        analytics: 'Analytics',
        services: 'Services',
        clients: 'Clients',
        staff: 'Staff',
        profile: 'Profile',
        settings: 'Settings',
        sectionMain: 'Main',
        sectionManagement: 'Management',
        sectionAccount: 'Account',
        signOut: 'Sign out',
        publicPage: 'Public page',
      },
      days: {
        monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday',
        thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday',
      },
      categories: {
        beautySalon: 'Beauty Salon',
        barber: 'Barber',
        dentalClinic: 'Dental Clinic',
        medicalClinic: 'Medical Clinic',
        spaWellness: 'Spa & Wellness',
        gymFitness: 'Gym / Fitness',
        photographer: 'Photographer',
        consultant: 'Consultant',
        lawyer: 'Lawyer',
        accountant: 'Accountant',
        aestheticCenter: 'Aesthetic Center',
        nailArtist: 'Nail Artist',
        other: 'Other',
      },
      header: {
        themeLight: 'Switch to light mode',
        themeDark: 'Switch to dark mode',
        themeLightLabel: 'Light',
        themeDarkLabel: 'Dark',
        copied: 'Copied!',
        copyLink: 'Copy link',
      },
      overview: {
        welcomePrefix: 'Welcome,',
        businessFallback: 'Business',
        subtitle: "Here's what's happening today",
        statTodayBookings: "Today's bookings",
        statTodaySub: 'today',
        statActiveBookings: 'Active bookings',
        statActiveSub: 'confirmed',
        statCompleted: 'Completed',
        statCompletedSub: 'services completed',
        statRevenue: 'Revenue',
        statRevenueSub: 'from completed bookings',
        chartLast7Days: 'Bookings — last 7 days',
        todayBookingsTitle: "Today's bookings",
        viewAll: 'View all →',
        noBookingsToday: 'No bookings today',
        quickActionAddService: 'Add new service',
        quickActionManageHours: 'Manage hours',
        quickActionViewPublic: 'View public profile',
        trialEndedTitle: 'Your free trial has ended.',
        trialEndingSoon: (days: number) => `Your Rezervo Online free trial ends in ${days} day${days === 1 ? '' : 's'}.`,
        trialEndedBody: 'Add payment details to keep using Rezervo Online.',
        trialEndingBody: 'Add a payment method to keep using the platform without interruption.',
        addCardCta: 'Add card →',
        trialInfoLabel: 'Free trial:',
        trialInfoDaysLeft: (days: number) => `${days} day${days === 1 ? '' : 's'} left`,
        trialInfoSuffix: '· You are using the 30-day free trial of Rezervo Online.',
        viewSubscription: 'View subscription →',
      },
      bookings: {
        title: 'Bookings',
        totalCount: (n: number) => `${n} booking${n === 1 ? '' : 's'} total`,
        searchPlaceholder: 'Search client...',
        emptyTitle: 'No bookings found.',
        confirmQuestion: 'Confirm?',
        confirmAction: 'Confirm',
        rejectAction: 'Reject',
        rejectConfirmQuestion: 'Reject?',
        editModalTitle: 'Edit booking',
        clientNameLabel: 'Client name *',
        emailLabel: 'Email',
        dateLabel: 'Date *',
        timeLabel: 'Time *',
        serviceLabelOptional: 'Service (optional — leave blank to keep current)',
        keepCurrentService: '— Keep current service —',
        notesLabel: 'Notes',
        saveChanges: 'Save changes',
      },
      waitingList: {
        title: 'Waiting List',
        summary: (total: number, waiting: number) => `${total} entr${total === 1 ? 'y' : 'ies'} total · ${waiting} waiting`,
        tabWaitingList: 'Waiting List',
        tabEarlierSlots: 'Earlier slots',
        waitingListHint: 'Clients with no booking who are waiting for availability. They are notified automatically when a booking is cancelled.',
        earlierSlotsHint: 'Clients with an active booking who want an earlier time if one becomes available.',
        filterAll: 'All',
        emptyWaitingList: 'The waiting list is empty.',
        emptyWaitingListFiltered: (label: string) => `No entries with status "${label}".`,
        emptyWaitingListHint: 'Clients can sign up from the public page when there are no open slots.',
        emptyEarlierSlots: 'No earlier-slot requests.',
        emptyEarlierSlotsHint: 'Clients choose this option after their booking is confirmed.',
        registered: (date: string) => `Registered: ${date}`,
        notifiedAt: (date: string) => `Notified: ${date}`,
        rescheduledAt: (date: string) => `Rescheduled: ${date}`,
        currentBooking: (date: string, time: string) => `Current booking: ${date} · ${time}`,
        notifyAction: 'Notify',
        bookedAction: 'Booked',
        expiredAction: 'Expired',
        statusActive: 'Active',
        statusInactive: 'Inactive',
        statusRescheduled: 'Rescheduled',
        statusNotified: 'Notified',
        waitingStatus: { waiting: 'Waiting', notified: 'Notified', booked: 'Booked', expired: 'Expired' },
      },
      tabError: {
        message: "We couldn't load this data.",
        retry: 'Retry',
      },
      clients: {
        title: 'Clients',
        uniqueCount: (n: number) => `${n} unique client${n === 1 ? '' : 's'}`,
        emptyTitle: 'No clients yet.',
        emptyHint: 'Clients appear automatically after they make a booking.',
        emailAction: 'Email',
        lastBooking: (date: string) => `Last booking: ${date}`,
      },
      staff: {
        title: 'Staff',
        activeCount: (n: number) => `${n} active member${n === 1 ? '' : 's'}`,
        addMember: '+ Add member',
        editMember: 'Edit member',
        newMember: 'New staff member',
        nameLabel: 'Name *',
        namePlaceholder: 'e.g. Arta Koci',
        roleLabel: 'Role *',
        rolePlaceholder: 'e.g. Stylist',
        emailLabel: 'Email',
        phoneLabel: 'Phone',
        colorLabel: 'Color',
        servicesPerformed: 'Services performed',
        weeklySchedule: "Staff member's weekly schedule",
        configureBusinessHoursFirst: '⚠️ Configure business hours first.',
        working: 'Working',
        onLeave: 'Off',
        businessClosedShort: 'Business closed',
        addFirstMember: '+ Add first member',
        noStaffYet: 'No staff members yet.',
        active: 'Active',
        inactive: 'Inactive',
      },
      schedule: {
        title: 'Calendar',
        selectDate: 'Select a date',
        serviceLabel: 'Service',
        allServices: 'All services',
        staffLabel: 'Staff',
        allStaff: 'All staff',
        addBooking: '+ Add booking',
        manage: '⚙ Manage',
        free: 'Free',
        booked: 'Booked',
        onLeaveLegend: 'Leave',
        outsideHours: 'Outside hours',
        closedLegend: 'Closed',
        businessClosurePrefix: 'Business closure',
        businessClosedPeriod: 'The business is closed for this period.',
        businessClosedDay: 'The business is closed this day.',
        noActiveStaff: 'No active staff',
        noActiveStaffForService: 'for this service',
        notWorkingToday: 'Not working today',
        onLeaveFullDay: '⏸ On leave all day',
        slotsFree: (n: number) => `${n} free`,
        slotsBooked: (n: number) => `${n} booked`,
        businessHours: 'Business hours',
        noSchedulesConfigured: 'No hours configured.',
        createDefaultSchedules: '+ Create default hours',
        creating: 'Creating...',
        minIntervalSuffix: 'min interval',
        open: 'Open',
        closed: 'Closed',
        addBookingModalTitle: '+ Add booking',
        bookingCreatedSuccess: 'Booking created successfully.',
        dateLabel: 'Date',
        timeLabel: 'Time',
        selectService: 'Select service',
        selectStaffRequired: 'Select staff (required)',
        noStaff: 'No staff',
        clientNameLabel: 'Client name *',
        clientNamePlaceholder: 'Name',
        phoneLabel: 'Phone',
        emailOptionalLabel: 'Email (optional)',
        manageModalTitle: 'Manage',
        businessClosureTab: 'Business Closure',
        staffLeaveTab: 'Staff Leave',
        closureIntro: 'Block the entire business for the selected period. Normal hours resume automatically afterward.',
        fromDate: 'From date *',
        toDate: 'To date *',
        reasonOptional: 'Reason (optional)',
        closureReasonPlaceholder: 'e.g. Annual leave',
        leaveReasonPlaceholder: 'e.g. Medical leave',
        leaveIntro: 'Add leave for a staff member. Normal hours resume automatically after this date.',
        staffMemberLabel: 'Staff member *',
        selectMember: 'Select member',
        leaveDateLabel: 'Leave date *',
        fullDay: 'Full day',
        specificHours: 'Specific hours',
        fromTime: 'From time',
        toTime: 'To time',
        conflictsWarning: (n: number) => `⚠️ ${n} existing booking(s) affected:`,
        confirmSaveAgain: 'Click "Save" again to confirm.',
        confirmAndSave: 'Confirm and Save',
        existing: 'Existing',
        staffRequiredError: 'Please select a staff member.',
        savedChanges: 'Changes saved.',
        endDateBeforeStartError: 'End date cannot be before start date.',
        allDatesAlreadyHaveLeave: 'All selected dates already have leave for this staff member.',
        savedWithSkippedDates: (saved: number, skipped: number) =>
          `Saved leave for ${saved} day${saved === 1 ? '' : 's'}; ${skipped} day${skipped === 1 ? '' : 's'} already had leave and were skipped.`,
      },
      services: {
        title: 'Services',
        count: (n: number) => `${n} service${n === 1 ? '' : 's'}`,
        addService: '+ Add service',
        editService: 'Edit service',
        newService: 'New service',
        nameLabel: 'Service name *',
        namePlaceholder: 'e.g. Haircut',
        priceLabel: 'Price (€)',
        optionalSuffix: '(optional)',
        pricePlaceholder: 'e.g. 15',
        durationLabel: 'Duration (min) *',
        descriptionLabel: 'Description (optional)',
        descriptionPlaceholder: 'Short description...',
        addServiceBtn: 'Add service',
        noServicesYet: 'No services yet.',
        addFirstService: '+ Add first service',
        active: 'Active',
        inactive: 'Inactive',
      },
      profile: {
        title: 'Business profile',
        subtitle: 'Information your clients see',
        photoLabel: 'Profile photo / Logo',
        uploadPhoto: 'Upload photo',
        changePhoto: 'Change photo',
        removePhoto: 'Remove photo',
        photoHint: 'JPG, PNG or WebP · Maximum 5MB',
        nameLabel: 'Business name',
        namePlaceholder: 'Studio Elegance',
        categoryLabel: 'Category',
        selectCategory: 'Select category',
        phoneLabel: 'Phone',
        websiteLabel: 'Website',
        addressLabel: 'Address',
        addressPlaceholder: 'Street, No.',
        cityLabel: 'City',
        cityPlaceholder: 'Tirana',
        descriptionLabel: 'Description',
        descriptionPlaceholder: 'Describe your business...',
        saveProfile: 'Save profile',
        saved: '✓ Saved!',
        savedSuccess: 'Changes saved successfully!',
        publicPreview: 'Public preview',
        businessNameFallback: 'Business name',
        bookingLink: 'Booking link:',
        copy: 'Copy',
      },
      settings: {
        title: 'Settings',
        subtitle: 'Account and business preferences',
        account: 'Account',
        businessFallback: 'My business',
        appearance: 'Dashboard appearance',
        displayMode: 'Display mode',
        homepageAlwaysDark: 'The homepage always stays Dark',
        notifications: 'Notifications',
        autoConfirm: 'Auto-confirm',
        autoConfirmOnDesc: 'New bookings are confirmed automatically.',
        autoConfirmOffDesc: 'New bookings stay "Pending" until you confirm or reject them yourself.',
        bookingLinkTitle: 'Booking link',
        bookingLinkDesc: 'Share this link with your clients to take bookings online.',
        copied: '✓ Copied',
        viewPublicPage: '↗ View public page',
        dangerZone: 'Danger zone',
        irreversibleActions: 'The following actions cannot be undone.',
        signOut: 'Sign out',
      },
      subscription: {
        title: 'Subscription',
        subtitle: 'Manage your Rezervo Online subscription',
        statusActive: 'Active subscription',
        statusTrial: '30-day free trial',
        statusPastDue: 'Payment overdue',
        statusCancelled: 'Subscription cancelled',
        statusPaymentRequired: 'Payment required',
        daysLeftFromTrial: (n: number) => `${n} day${n === 1 ? '' : 's'} left in your free trial`,
        trialEnded: 'Free trial has ended',
        fullyActive: 'The platform is fully active',
        addPaymentToContinue: 'Add a payment method to continue',
        badgeTrial: 'Trial',
        badgeActive: 'Active',
        badgePastDue: 'Past Due',
        badgeCancelled: 'Cancelled',
        badgePaymentRequired: 'Payment required',
        trialProgress: 'Trial progress',
        daysOf30: (n: number) => `${n}/30 days`,
        started: 'Started',
        ends: 'Ends',
        trialBodyText: 'You are using the 30-day free trial of Rezervo Online. You can add a payment method at any time during the trial — it is not required at signup.',
        activeBodyText: 'Your subscription is active. You have full access to every platform feature.',
        inactiveBodyText: 'Your free trial has ended. Add payment details to regain full access. All your data has been kept.',
        activatedSuccess: 'Subscription activated successfully!',
        paypalConfirmingText: 'Your payment was accepted by PayPal. Your subscription status will update automatically after system verification.',
        activateWithPaypal: '💳 Activate subscription with PayPal',
        priceLine: (price: string) => `€${price} / month · Auto-renews · Cancel anytime`,
        retryLoadingPaypal: '🔄 Try again',
        loadingPaypal: 'Loading PayPal...',
        paypalGenericError: 'Payment failed. Please try again or contact support.',
        paypalCancelledError: 'Payment was cancelled or the window was closed before approval. Click "Try again" if you want to continue.',
        paypalSecureNote: '🔒 Payments are processed securely by PayPal Sandbox. Card details are never stored by Rezervo Online.',
        supabaseConfigMissing: 'Supabase configuration is missing.',
        paypalConfigMissing: 'PayPal configuration is missing. Please contact support.',
        paypalLoadError: 'Error loading PayPal. Please try again.',
        whatsIncluded: 'What Rezervo Online includes',
        features: [
          'Unlimited online bookings',
          'Full dashboard with analytics',
          'Staff and service management',
          'Automatic waiting list',
          'Email notifications',
          'Customized public page',
        ],
      },
      analytics: {
        title: 'Analytics',
        subtitle: 'Booking and revenue statistics',
        period7d: '7 days',
        period30d: '30 days',
        period90d: '90 days',
        totalBookings: 'Total bookings',
        revenue: 'Revenue',
        completionRate: 'Completion rate',
        uniqueClients: 'Unique clients',
        dailyBookings: 'Daily bookings',
        bookingStatus: 'Booking status',
        noData: 'No data',
        dailyRevenue: 'Daily revenue (€)',
        topServices: 'Top services',
        chartBookingsLabel: 'Bookings',
        chartRevenueLabel: 'Revenue',
      },
      errors: {
        saveFailed: 'Save failed.',
        loadFailed: 'Failed to load data.',
        uploadFailed: 'Upload failed.',
        createFailed: 'Creation failed.',
        photoTooLarge: 'The photo must not exceed 5MB.',
        nameRequired: 'Member name is required.',
        roleRequired: 'Role is required.',
        staffSaveFailed: 'Save failed. Please try again.',
        businessClosedAt: (day: string) => `The business is closed on ${day}`,
        openBeforeClose: 'Start time must be before end time',
        staffHoursWithinBusiness: (open: string, close: string) => `Staff hours must be within ${open}–${close}`,
      },
      addBookingErrors: {
        clientNameRequired: 'Client name is required.',
        businessNotFound: 'Business not found.',
        serviceNotFound: 'Service not found or inactive.',
        dateInPast: 'Cannot book a past date.',
        slotInPast: 'This time has already passed today. Choose another time.',
        businessClosed: 'Business is closed for this period.',
        businessClosedThatDay: 'Business is closed this day.',
        outsideBusinessHours: 'Selected time is outside working hours.',
        staffRequired: 'Please choose a staff member.',
        staffNotFound: 'Selected staff member not found.',
        staffNotEligible: 'This staff member cannot perform this service.',
        noAvailability: 'This time is taken. Choose another time.',
        notAuthorized: 'Not authorized for this action.',
        generic: 'Something went wrong. Please try again.',
        fillRequiredFields: 'Please fill in date, time, and service.',
      },
    },
    legal: {
      lastUpdated: 'Last updated: September 1, 2026',
      footerTerms: 'Terms',
      footerPrivacy: 'Privacy',
      contactHeading: 'Contact',
      contactIntro: 'For questions about this page, contact us at:',
      bookingNotice: {
        prefix: 'By continuing with the booking, your information will be processed to manage your reservation. See our ',
        linkText: 'Privacy Policy',
        suffix: '.',
      },
      registerNotice: {
        prefix: 'By creating an account, you agree to our ',
        termsLink: 'Terms',
        middle: ' and confirm you have read our ',
        privacyLink: 'Privacy Policy',
        suffix: '.',
      },
      terms: {
        title: 'Terms & Conditions',
        intro: 'These Terms govern the use of the Rezervo Online platform by the businesses that use it.',
        sections: {
          about: {
            heading: 'About the Service',
            body: [
              'Rezervo Online is an online booking-management SaaS platform for businesses.',
              'Businesses create and manage their own services, staff, schedules, and bookings through the platform.',
              'Rezervo Online is not the provider of the underlying service (beauty, medical, professional, etc.) booked by the end customer — that service is provided by the business that created the booking.',
            ],
          },
          responsibilities: {
            heading: 'Business Responsibilities',
            body: [
              'The business user is responsible for the accuracy of their own information, availability, services offered, and obligations toward their own customers.',
            ],
          },
          subscription: {
            heading: 'Subscription & Billing',
            body: [
              'We offer a 30-day free trial.',
              'The paid subscription price is €27/month.',
              'Once activated, the subscription renews automatically every month until cancelled.',
              'Payments are processed by PayPal. Rezervo Online does not store raw card/payment credentials.',
              'If the subscription or trial is not active, accepting new online bookings may be temporarily disabled, while account/billing access may remain available.',
              'You can cancel your subscription at any time. Cancellation stops future renewal; access continues according to the period already paid for, as the product currently works.',
              'Amounts already charged are generally non-refundable, except where required by applicable law or expressly agreed otherwise.',
            ],
          },
          acceptableUse: {
            heading: 'Acceptable Use',
            body: [
              'The platform may only be used for lawful business purposes. Abusive or fraudulent use, or use that harms the platform or other users, is not permitted.',
            ],
          },
          accountSecurity: {
            heading: 'Account Security',
            body: [
              'You are responsible for keeping your account credentials confidential and for all activity that occurs under your account.',
            ],
          },
          availability: {
            heading: 'Availability & Service Interruptions',
            body: [
              'We make reasonable efforts to keep the platform available, but we do not guarantee uninterrupted operation. Maintenance or temporary technical interruptions may occur.',
            ],
          },
          liability: {
            heading: 'Limitation of Liability',
            body: [
              'To the maximum extent permitted by law, Rezervo Online is not liable for indirect, incidental, or consequential damages arising from use of the platform, including the relationship between a business and its own customers.',
            ],
          },
          termination: {
            heading: 'Termination',
            body: [
              'We may suspend or terminate access in the event of a breach of these Terms. You may close your own account at any time.',
            ],
          },
          changes: {
            heading: 'Changes to These Terms',
            body: [
              'These Terms may be updated from time to time. Continued use of the platform after changes means you accept the updated Terms.',
            ],
          },
          governingLaw: {
            heading: 'Governing Law',
            body: [
              'These Terms are governed by the laws of the Republic of Albania.',
            ],
          },
        },
      },
      privacy: {
        title: 'Privacy Policy',
        intro: 'This policy explains how Rezervo Online collects, uses, and protects personal data, in line with Law No. 124/2024 "On the Protection of Personal Data".',
        sections: {
          dataCollected: {
            heading: 'Data We Process',
            body: [
              'Business account data: name, email, phone, business/profile information, and subscription/payment-status information.',
              'Booking customer data: name, email, phone, booking date/time, selected service, and selected staff member (where applicable).',
            ],
          },
          purposes: {
            heading: 'Purposes of Processing',
            body: [
              'We use this data for: operating your account, managing bookings, sending transactional emails, notifications, security, billing/subscription administration, and customer support.',
            ],
          },
          lawfulBasis: {
            heading: 'Lawful Basis for Processing',
            body: [
              'We process data only when necessary to provide the service (contract performance), to comply with legal obligations, or based on our legitimate interest in the security and operation of the platform.',
            ],
          },
          controllerProcessor: {
            heading: 'Controller / Processor Roles',
            body: [
              'Rezervo Online acts as controller for business-account, subscription, security, and service-administration data.',
              'For customer data that a business processes through Rezervo Online, the business determines the purpose of the booking/service, while Rezervo Online processes this data to provide the platform.',
            ],
          },
          thirdParties: {
            heading: 'Third-Party Service Providers',
            body: [
              'We use trusted providers to operate the service: Supabase (database/authentication/backend), Resend (transactional email delivery), PayPal (subscription/payment processing), and Netlify (website hosting).',
              'Raw card/payment credentials are handled directly by the payment provider and are not stored by Rezervo Online.',
            ],
          },
          dataSharing: {
            heading: 'Data Sharing',
            body: [
              'We do not sell personal data. We share data only with the service providers listed above, to the extent necessary to operate the platform, or where required by law.',
            ],
          },
          international: {
            heading: 'International Processing',
            body: [
              'Some of our service providers may process data outside Albania. In these cases, we aim to ensure processing takes place with appropriate safeguards in place.',
            ],
          },
          security: {
            heading: 'Security',
            body: [
              'We take reasonable technical and organizational measures to protect personal data. No system can guarantee absolute security.',
            ],
          },
          retention: {
            heading: 'Data Retention',
            body: [
              'We retain data for as long as necessary for the purposes described above, or as required by law.',
            ],
          },
          rights: {
            heading: 'Your Rights',
            body: [
              'Under applicable law, you have the right to request access, correction, deletion, or restriction of processing of your personal data, as well as other rights provided under Law No. 124/2024. To exercise these rights, contact us at the address below.',
            ],
          },
          cookies: {
            heading: 'Cookies & Technical Storage',
            body: [
              'We use local technical storage (such as cookies/localStorage) only for essential functions, such as authentication and remembering preferences (e.g. site language).',
            ],
          },
          changes: {
            heading: 'Changes to This Policy',
            body: [
              'This policy may be updated from time to time. Material changes will be reflected on this page.',
            ],
          },
        },
      },
    },
    register: {
      tagline: '30 days free • No credit card',
      stepLabels: ['Account', 'Information', 'Hours'],
      alreadyHaveAccount: 'Already have an account?',
      loginHere: 'Log in here',
      continueCta: 'Continue →',
      savingCta: 'Saving...',
      step1: {
        title: 'Register your business',
        subtitle: 'Step 1 of 3 — Account',
        businessNameLabel: 'Business name *',
        businessNamePlaceholder: 'e.g. Studio Elegance',
        emailLabel: 'Email *',
        emailPlaceholder: 'email@business.com',
        phoneLabel: 'Phone number',
        phonePlaceholder: '+355 69 000 0000',
        passwordLabel: 'Password *',
        passwordPlaceholder: 'Minimum 6 characters',
        creatingAccountCta: 'Creating account...',
      },
      step2: {
        title: 'Business details',
        subtitle: 'Step 2 of 3 — Business information',
        categoryLabel: 'Business category *',
        categoryPlaceholder: 'Select category...',
        cityLabel: 'City *',
        cityPlaceholder: 'e.g. Tirana',
        addressLabel: 'Address',
        addressPlaceholder: 'e.g. Myslym Shyri Street, No. 5',
        descriptionLabel: 'Description (optional)',
        descriptionPlaceholder: 'Briefly describe your business...',
      },
      step3: {
        title: 'Business hours',
        subtitle: 'Step 3 of 3 — General business working hours',
        bookingIntervalLabel: 'Global booking start interval',
        bookingIntervalHelp: 'Controls the possible booking start times (e.g. 30 min → 09:00, 09:30, 10:00...)',
        weeklyScheduleLabel: 'Weekly schedule',
        closedLabel: 'Closed',
        finishCta: 'Finish registration →',
      },
      errors: {
        businessNameRequired: 'Business name is required.',
        emailRequired: 'Email is required.',
        passwordTooShort: 'Password must be at least 6 characters.',
        categoryRequired: 'Please select the business category.',
        cityRequired: 'City is required.',
        businessIdMissing: 'Error: business ID is missing.',
        generic: 'Something went wrong. Please try again.',
        authenticationRequired: 'Your session has expired. Please try again.',
        businessAlreadyExists: 'This account already has a registered business.',
        slugGenerationFailed: 'Something went wrong during registration. Please try again.',
      },
    },
    admin: {
      nav: { overview: 'Overview', businesses: 'Businesses' },
      header: { title: 'Rezervo Admin', badge: 'PLATFORM ADMIN', backToDashboard: 'Back to dashboard' },
      accessDenied: {
        title: 'Access restricted',
        body: 'You do not have access to the platform admin dashboard.',
        backToDashboard: 'Back to my dashboard',
      },
      loading: 'Loading...',
      loadError: 'Something went wrong while loading. Please try again.',
      overview: {
        totalBusinesses: 'Total Businesses',
        testBusinesses: 'Test Businesses',
        trialBusinesses: 'Trial Businesses',
        activeBusinesses: 'Active / Paid Businesses',
        expiredBlockedBusinesses: 'Expired / Blocked Businesses',
        totalBookings: 'Total Bookings',
        totalCustomers: 'Total Customers',
        totalServices: 'Total Services',
        totalStaff: 'Total Staff',
        bookingsToday: 'Bookings Today',
        bookingsLast7Days: 'Bookings (Last 7 Days)',
        newBusinessesLast30Days: 'New Businesses (Last 30 Days)',
      },
      businesses: {
        title: 'Businesses',
        searchPlaceholder: 'Search by business name or owner email...',
        filterAll: 'All',
        filterTrial: 'Trial',
        filterActive: 'Active',
        filterExpiredBlocked: 'Expired/Blocked',
        filterTest: 'Test',
        categoryAll: 'All categories',
        columns: {
          name: 'Business',
          owner: 'Owner',
          phone: 'Phone',
          category: 'Category',
          city: 'City',
          created: 'Created',
          trialEnd: 'Trial End',
          status: 'Status',
          bookingStatus: 'Bookings',
          paypal: 'PayPal',
          test: 'Test',
          services: 'Services',
          staff: 'Staff',
          bookings: 'Bookings',
          customers: 'Customers',
        },
        empty: 'No businesses match the current search/filter.',
        prev: '← Previous',
        next: 'Next →',
        pageInfo: (from: number, to: number, total: number) => `${from}–${to} of ${total}`,
        viewDetail: 'View details',
        none: '—',
      },
      status: { trial: 'Trial', active: 'Active', expiredBlocked: 'Blocked', test: 'Test', enabled: 'Enabled', blocked: 'Blocked' },
      detail: {
        title: 'Business details',
        basic: 'Basic',
        subscription: 'Subscription',
        usage: 'Usage',
        slug: 'Slug',
        owner: 'Owner email',
        phone: 'Phone',
        category: 'Category',
        city: 'City',
        created: 'Created',
        website: 'Website',
        none: '—',
        trialStart: 'Trial start',
        trialEnd: 'Trial end',
        status: 'Subscription status',
        bookingStatus: 'Booking status',
        paypal: 'PayPal subscription ID',
        testFlag: 'Test business',
        totalBookings: 'Total bookings',
        bookingsLast30Days: 'Bookings (last 30 days)',
        clients: 'Clients (unique)',
        services: 'Services',
        staff: 'Staff',
        waitingList: 'On waiting list',
        lastBooking: 'Last booking',
        never: 'Never',
        openPublicPage: 'Open public page →',
        close: 'Close',
      },
    },
    pwa: {
      dashboardCard: {
        title: 'Install Rezervo',
        body: "Open your dashboard directly from your phone's Home Screen.",
        androidCta: 'Install Rezervo',
        iosCta: 'Add to Home Screen',
        installedLabel: '✓ Rezervo is installed',
      },
      iosSheet: {
        title: 'Install Rezervo on iPhone',
        step1: 'Tap Share ⬆️ in Safari',
        step2: 'Choose "Add to Home Screen"',
        step3: 'Tap "Add"',
        footer: 'Then open Rezervo directly from your Home Screen.',
      },
      registerSuccess: {
        heading: 'Rezervo is ready 🎉',
        body: 'Add Rezervo to your phone and open your dashboard with one tap.',
        continueCta: 'Continue to Dashboard',
      },
    },
  },
};
